import {
  doc,
  onSnapshot,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  deleteField,
} from "firebase/firestore";
import { db } from "../backend/firebaseConfig";
import { buildStandardHalfHourSlotLabels, isBlockingAppointmentStatus } from "./chairScheduling";
import {
  computeCompressibleTimelineShiftUpdates,
  formatClock12FromMinutes,
  getAppointmentDisplayRangeMinutes,
  isCompressiblePendingStatus,
  LAST_MINUTE_OF_CALENDAR_DAY,
  parseClockLabelToMinutes,
} from "./appointmentTimeDisplay";

export const DENTIST_SCHEDULES_COLLECTION = "dentistSchedules";
export const CLINIC_SCHEDULING_COLLECTION = "clinicScheduling";
export const DENTIST_PLOT_DOC_PREFIX = "dentistPlot_";
export const CHAIR_SCHEDULES_DOC_PATH = ["clinicScheduling", "chairSchedulesByDate"];
export const MAX_CLINIC_CHAIRS = 20;

export const STANDARD_OFFICE_SLOT_LABELS = buildStandardHalfHourSlotLabels({});

export function getLocalDateKeyFromDate(date = new Date()) {
  const localDate = new Date(date);
  localDate.setHours(0, 0, 0, 0);
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, "0");
  const day = String(localDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDentistPlotDocId(dateKey) {
  return `${DENTIST_PLOT_DOC_PREFIX}${dateKey}`;
}

function getDentistPlotDocRef(dateKey) {
  return doc(db, CLINIC_SCHEDULING_COLLECTION, getDentistPlotDocId(dateKey));
}

function coerceAvailableSlotsArray(slots) {
  if (Array.isArray(slots)) return slots;
  if (slots && typeof slots === "object") {
    return Object.values(slots).filter((v) => typeof v === "string");
  }
  return [];
}

/** Map arbitrary slot labels onto the standard half-hour office labels. */
export function normalizeAvailableSlots(slots = []) {
  const input = coerceAvailableSlotsArray(slots);
  if (input.length === 0) return [];

  const allowed = new Set(STANDARD_OFFICE_SLOT_LABELS);
  const matched = new Set();

  input.forEach((slot) => {
    const trimmed = String(slot || "").trim();
    if (!trimmed) return;
    if (allowed.has(trimmed)) {
      matched.add(trimmed);
      return;
    }
    const parsed = parsePlottedSlotLabelRange(trimmed);
    if (!parsed) return;
    STANDARD_OFFICE_SLOT_LABELS.forEach((label) => {
      const standard = parsePlottedSlotLabelRange(label);
      if (
        standard &&
        standard.start === parsed.start &&
        standard.end === parsed.end
      ) {
        matched.add(label);
      }
    });
  });

  return STANDARD_OFFICE_SLOT_LABELS.filter((label) => matched.has(label));
}

const DENTIST_PLOT_DOC_META_KEYS = new Set(["updatedAt", "updatedByUid"]);

/** Strip Firestore metadata so only dentist uid entries remain. */
export function sanitizeDentistPlotDocData(docData) {
  const out = {};
  if (!docData || typeof docData !== "object") return out;
  Object.entries(docData).forEach(([key, value]) => {
    if (!key || DENTIST_PLOT_DOC_META_KEYS.has(key)) return;
    if (!value || typeof value !== "object" || Array.isArray(value)) return;
    out[key] = value;
  });
  return out;
}

export function computeUnavailableSlotsFromAvailable(availableSlots = []) {
  const availableSet = new Set(normalizeAvailableSlots(availableSlots));
  return STANDARD_OFFICE_SLOT_LABELS.filter((label) => !availableSet.has(label));
}

export function getDentistScheduleEntry(scheduleDocData, dentistUid) {
  if (!scheduleDocData || !dentistUid) return null;
  const entry = scheduleDocData[dentistUid];
  if (!entry || typeof entry !== "object") return null;
  const availableSlots = normalizeAvailableSlots(entry.availableSlots);
  if (availableSlots.length === 0) return null;
  const chairNumber = Number(entry.chairNumber);
  return {
    chairNumber: Number.isFinite(chairNumber) && chairNumber > 0 ? chairNumber : null,
    availableSlots,
  };
}

export function dentistHasPlottedSchedule(scheduleDocData, dentistUid) {
  return Boolean(getDentistScheduleEntry(scheduleDocData, dentistUid));
}

/** Parsed minute ranges for each plotted slot label (staff Set Dentist Schedule). */
export function getPlottedScheduleRangesForDentist(scheduleDocData, dentistUid) {
  const entry = getDentistScheduleEntry(scheduleDocData, dentistUid);
  if (!entry) return [];
  return entry.availableSlots
    .map((label) => {
      const range = parsePlottedSlotLabelRange(label);
      if (!range) return null;
      return { start: range.start, end: range.end, label };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);
}

/** True when staff have configured chairs for the plot-based schedule workflow. */
export function isDentistSchedulePlottingEnabled(settings) {
  if (!settings) return false;
  const count = Number(settings.chairCount);
  return Number.isFinite(count) && count > 0;
}

export function getChairScheduleEntry(docData) {
  if (!docData || typeof docData !== "object") return null;
  const count = Number(docData.chairCount);
  if (!Number.isFinite(count) || count <= 0) return null;
  return { chairCount: count };
}

const CHAIR_SCHEDULE_DATE_KEY_RE = /^(\d{4}-\d{2}-\d{2})$/;
const CHAIR_SCHEDULE_LEGACY_FIELD_RE = /^schedules\.(\d{4}-\d{2}-\d{2})$/;

/** Normalize schedule map keys (handles legacy `schedules.YYYY-MM-DD` keys). */
function parseChairScheduleDateKey(rawKey) {
  if (!rawKey || typeof rawKey !== "string") return null;
  const trimmed = rawKey.trim();
  const legacy = trimmed.match(/^schedules\.(\d{4}-\d{2}-\d{2})$/);
  if (legacy) return legacy[1];
  return CHAIR_SCHEDULE_DATE_KEY_RE.test(trimmed) ? trimmed : null;
}

function buildChairSchedulesMapFromDocData(docData) {
  const byDate = new Map();
  if (!docData || typeof docData !== "object") return byDate;

  const schedules = docData.schedules;
  if (schedules && typeof schedules === "object" && !Array.isArray(schedules)) {
    Object.entries(schedules).forEach(([rawKey, count]) => {
      const dateKey = parseChairScheduleDateKey(rawKey);
      if (!dateKey) return;
      const parsed = Number(count);
      if (Number.isFinite(parsed) && parsed > 0) {
        byDate.set(dateKey, parsed);
      }
    });
  }

  // Legacy mistaken top-level fields from older dot-notation writes.
  Object.entries(docData).forEach(([fieldKey, count]) => {
    const match = fieldKey.match(CHAIR_SCHEDULE_LEGACY_FIELD_RE);
    if (!match) return;
    const parsed = Number(count);
    if (Number.isFinite(parsed) && parsed > 0 && !byDate.has(match[1])) {
      byDate.set(match[1], parsed);
    }
  });

  return byDate;
}

function normalizeChairSchedulesMap(docData) {
  return buildChairSchedulesMapFromDocData(docData);
}

export function resolveChairCountForDate({
  dateChairSchedule = null,
  globalSettings = null,
  maxChairs = null,
} = {}) {
  const dateCount = Number(dateChairSchedule?.chairCount);
  let resolved = 0;
  if (Number.isFinite(dateCount) && dateCount > 0) {
    resolved = dateCount;
  } else {
    const globalCount = Number(globalSettings?.chairCount);
    if (Number.isFinite(globalCount) && globalCount > 0) {
      resolved = globalCount;
    }
  }
  if (resolved <= 0) return 0;
  const cap = Number(maxChairs);
  if (Number.isFinite(cap) && cap > 0) {
    return Math.min(resolved, cap);
  }
  return resolved;
}

/** True only when staff saved a chair count for this specific date (no global fallback). */
export function isChairScheduleActiveForDate(dateChairSchedule, _globalSettings = null) {
  void _globalSettings;
  const dateCount = Number(dateChairSchedule?.chairCount);
  return Number.isFinite(dateCount) && dateCount > 0;
}

export function subscribeToChairScheduleForDate(dateKey, callback) {
  if (!dateKey || typeof callback !== "function") {
    return () => {};
  }
  const ref = doc(db, ...CHAIR_SCHEDULES_DOC_PATH);
  return onSnapshot(
    ref,
    (snapshot) => {
      const byDate = normalizeChairSchedulesMap(snapshot.exists() ? snapshot.data() : null);
      const count = byDate.get(dateKey);
      callback(count ? { chairCount: count } : null);
    },
    () => callback(null)
  );
}

/** Real-time map of dateKey -> chairCount for all configured chair schedules. */
export function subscribeToChairScheduledDates(callback) {
  if (typeof callback !== "function") {
    return () => {};
  }
  const ref = doc(db, ...CHAIR_SCHEDULES_DOC_PATH);
  return onSnapshot(
    ref,
    (snapshot) => {
      callback(normalizeChairSchedulesMap(snapshot.exists() ? snapshot.data() : null));
    },
    () => callback(new Map())
  );
}

export async function saveChairScheduleForDates({
  dateKeys = [],
  chairCount,
  updatedByUid = null,
}) {
  const count = Number(chairCount);
  if (!Number.isFinite(count) || count < 0) {
    throw new Error("Invalid chair count.");
  }
  if (!Array.isArray(dateKeys) || dateKeys.length === 0) {
    throw new Error("Please select at least one date.");
  }

  const ref = doc(db, ...CHAIR_SCHEDULES_DOC_PATH);
  const snap = await getDoc(ref);
  const docData = snap.exists() ? snap.data() || {} : {};

  const cleanSchedules = Object.fromEntries(
    buildChairSchedulesMapFromDocData(docData).entries()
  );

  const legacyFieldDeletes = {};
  const nestedLegacyDeletes = {};

  dateKeys.forEach((dateKey) => {
    if (!CHAIR_SCHEDULE_DATE_KEY_RE.test(dateKey)) return;
    if (count <= 0) {
      delete cleanSchedules[dateKey];
      // Remove the nested date entry when clearing to "None".
      nestedLegacyDeletes[`schedules.${dateKey}`] = deleteField();
    } else {
      cleanSchedules[dateKey] = count;
    }
    // Remove mistaken top-level fields from older dot-notation writes (not the nested map).
    legacyFieldDeletes[`schedules.${dateKey}`] = deleteField();
  });

  // Drop malformed keys that were stored inside `schedules` (e.g. schedules.2026-05-31).
  const rawSchedules = docData.schedules;
  if (rawSchedules && typeof rawSchedules === "object" && !Array.isArray(rawSchedules)) {
    Object.keys(rawSchedules).forEach((rawKey) => {
      const normalized = parseChairScheduleDateKey(rawKey);
      if (!normalized || rawKey === normalized) return;
      nestedLegacyDeletes[`schedules.${rawKey}`] = deleteField();
    });
  }

  Object.keys(docData).forEach((fieldKey) => {
    if (CHAIR_SCHEDULE_LEGACY_FIELD_RE.test(fieldKey)) {
      legacyFieldDeletes[fieldKey] = deleteField();
    }
  });

  const payload = {
    schedules: cleanSchedules,
    updatedAt: new Date().toISOString(),
    ...(updatedByUid ? { updatedByUid } : {}),
    ...legacyFieldDeletes,
  };

  if (snap.exists()) {
    await updateDoc(ref, payload);
    if (Object.keys(nestedLegacyDeletes).length > 0) {
      await updateDoc(ref, nestedLegacyDeletes);
    }
  } else {
    await setDoc(ref, payload, { merge: true });
  }
}

function parsePlottedSlotLabelRange(label) {
  if (!label || typeof label !== "string") return null;
  const [startRaw, endRaw] = label.split(" - ").map((part) => part.trim());
  const start = parseClockLabelToMinutes(startRaw);
  const end = parseClockLabelToMinutes(endRaw);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
  return { start, end };
}

function plottedSlotRangesOverlap(a, b) {
  return Boolean(a && b && a.start < b.end && a.end > b.start);
}

function getAppointmentRangeMinutesForChair(appointment) {
  if (!appointment || typeof appointment !== "object") return null;
  // Use booked Firestore times only — not display compression overrides (_dStart/_dEnd).
  const start = parseClockLabelToMinutes(
    appointment.startTime || appointment.time || ""
  );
  if (!Number.isFinite(start)) return null;
  const duration = Number(appointment.duration) || 30;
  const end = appointment.endTime
    ? parseClockLabelToMinutes(appointment.endTime)
    : start + duration;
  if (!Number.isFinite(end) || end <= start) return null;
  return { start, end };
}

function appointmentOverlapsRange(appointment, rangeStart, rangeEnd) {
  const range = getAppointmentRangeMinutesForChair(appointment);
  if (!range) return false;
  return range.start < rangeEnd && range.end > rangeStart;
}

function resolveAppointmentDentistUid(appointment = {}) {
  return (
    appointment.dentistId ||
    appointment.dentistUID ||
    appointment.dentistUid ||
    null
  );
}

function appointmentMatchesId(appointment, targetId) {
  if (!appointment || !targetId) return false;
  const id = appointment.id || appointment.appointmentId;
  return id === targetId;
}

function chairHasOverlapAtRange(chair, startMin, endMin) {
  return (Array.isArray(chair?.appointments) ? chair.appointments : []).some(
    (existing) => appointmentOverlapsRange(existing, startMin, endMin)
  );
}

function findChairByNumber(chairs, chairNum) {
  const target = Number(chairNum);
  if (!Number.isFinite(target) || target <= 0) return null;
  return (Array.isArray(chairs) ? chairs : []).find(
    (entry) => entry.chairNum === target
  );
}

/**
 * Assign non-cancelled appointments to chairs at their booked times.
 * When schedule plots exist, each appointment is placed on its dentist's plotted
 * chair first so patient booking does not block other dentists on free chairs.
 */
export function assignAppointmentsToChairsForBooking({
  appointments = [],
  chairCount = 0,
  scheduleDocData = null,
  preferExistingChairAnchors = true,
  /** When true, place by clock time only (dashboard display — avoids confirmed-first spillover). */
  sortByClockTimeOnly = false,
} = {}) {
  const count = Number(chairCount);
  if (!Number.isFinite(count) || count <= 0) return [];

  const chairs = Array.from({ length: count }, (_, idx) => ({
    chairNum: idx + 1,
    appointments: [],
  }));

  const getBookingTs = (appt) => {
    const id = appt.appointmentId || appt.id || "";
    const lastUnderscore = id.lastIndexOf("_");
    if (lastUnderscore === -1) return 0;
    const ts = Number(id.slice(lastUnderscore + 1));
    return Number.isFinite(ts) ? ts : 0;
  };

  const active = (Array.isArray(appointments) ? appointments : []).filter((appt) => {
    const status = (appt?.status || "").toLowerCase();
    return status !== "cancelled" && status !== "canceled";
  });

  const sorted = [...active].sort((a, b) => {
    if (!sortByClockTimeOnly) {
      const aPending = isCompressiblePendingStatus(a.status);
      const bPending = isCompressiblePendingStatus(b.status);
      if (aPending !== bPending) return aPending ? 1 : -1;
    }

    if (preferExistingChairAnchors) {
      const aDentistUid = resolveAppointmentDentistUid(a);
      const bDentistUid = resolveAppointmentDentistUid(b);
      const aHasAnchor = Boolean(
        aDentistUid && getDentistScheduleEntry(scheduleDocData, aDentistUid)?.chairNumber
      );
      const bHasAnchor = Boolean(
        bDentistUid && getDentistScheduleEntry(scheduleDocData, bDentistUid)?.chairNumber
      );
      if (aHasAnchor !== bHasAnchor) return aHasAnchor ? -1 : 1;
    }

    const aRange = getAppointmentRangeMinutesForChair(a);
    const bRange = getAppointmentRangeMinutesForChair(b);
    const aMin = aRange?.start ?? 0;
    const bMin = bRange?.start ?? 0;
    if (aMin !== bMin) return aMin - bMin;
    return getBookingTs(a) - getBookingTs(b);
  });

  sorted.forEach((appt) => {
    const range = getAppointmentRangeMinutesForChair(appt);
    if (!range) return;
    const { start: wStart, end: wEnd } = range;

    let placed = false;
    const dentistUid = resolveAppointmentDentistUid(appt);
    const plottedChair =
      dentistUid != null
        ? getDentistScheduleEntry(scheduleDocData, dentistUid)?.chairNumber ?? null
        : null;
    const preferredChair = findChairByNumber(chairs, plottedChair);
    if (
      preferredChair &&
      !chairHasOverlapAtRange(preferredChair, wStart, wEnd)
    ) {
      preferredChair.appointments.push(appt);
      placed = true;
    }

    if (!placed) {
      for (const chair of chairs) {
        if (chairHasOverlapAtRange(chair, wStart, wEnd)) continue;
        chair.appointments.push(appt);
        placed = true;
        break;
      }
    }

    if (!placed) {
      // No free chair at the booked time — count as overlap on chair 1 for capacity checks.
      chairs[0].appointments.push(appt);
    }
  });

  return chairs;
}

const HARD_CHAIR_BLOCK_STATUSES = new Set(["confirmed", "completed", "reserved"]);

function isHardChairBlockStatus(status = "") {
  return HARD_CHAIR_BLOCK_STATUSES.has(String(status || "").trim().toLowerCase());
}

/**
 * When all chairs are occupied in [startMin, endMin), returns whether the block is
 * hard (confirmed/completed/reserved on any chair) or soft (pending-only).
 */
export function getChairBlockSeverityAtRange(chairTableData, startMin, endMin) {
  if (!areAllChairsOccupiedAtRange(chairTableData, startMin, endMin)) return null;
  for (const { appointments } of Array.isArray(chairTableData) ? chairTableData : []) {
    for (const appt of Array.isArray(appointments) ? appointments : []) {
      if (
        appointmentOverlapsRange(appt, startMin, endMin) &&
        isHardChairBlockStatus(appt.status)
      ) {
        return "chair-full";
      }
    }
  }
  return "chair-full-soft";
}

/** Block severity for a single chair at [startMin, endMin). */
export function getChairBlockSeverityForChair(chairTableData, chairNum, startMin, endMin) {
  const chair = Number(chairNum);
  if (!Number.isFinite(chair) || chair <= 0) return null;
  const bucket = (Array.isArray(chairTableData) ? chairTableData : []).find(
    (entry) => entry.chairNum === chair
  );
  if (!bucket) return null;
  let hasOverlap = false;
  for (const appt of Array.isArray(bucket.appointments) ? bucket.appointments : []) {
    if (!appointmentOverlapsRange(appt, startMin, endMin)) continue;
    hasOverlap = true;
    if (isHardChairBlockStatus(appt.status)) return "chair-full";
  }
  return hasOverlap ? "chair-full-soft" : null;
}

function cloneAppointmentWithMinuteRange(appt, startMin, endMin) {
  const duration = Math.max(1, endMin - startMin);
  const startLabel = formatClock12FromMinutes(startMin);
  const endLabel = formatClock12FromMinutes(endMin);
  return {
    ...appt,
    startTime: startLabel,
    time: startLabel,
    endTime: endLabel,
    duration,
  };
}

function rangeOverlapsScheduleUnavailable(startMin, endMin, unavailableRanges) {
  if (!Array.isArray(unavailableRanges) || unavailableRanges.length === 0) return false;
  return unavailableRanges.some((r) => startMin < r.end && endMin > r.start);
}

/**
 * Chair-plot patient booking: allow longer service duration when pending visits on the
 * same chair can shift later, as long as each affected dentist remains within plotted hours.
 */
export function planPatientBookingOnChairPlot({
  appointments = [],
  chairCount = 0,
  scheduleDocData = null,
  bookingDentistId = null,
  newStartMin,
  newEndMin,
} = {}) {
  const cc = Number(chairCount);
  if (!Number.isFinite(cc) || cc <= 0) {
    return { ok: true, message: "", pushUpdates: [] };
  }
  if (
    !Number.isFinite(newStartMin) ||
    !Number.isFinite(newEndMin) ||
    newEndMin <= newStartMin
  ) {
    return { ok: true, message: "", pushUpdates: [] };
  }

  if (bookingDentistId) {
    const bookingUnavail = getDentistScheduleUnavailableRanges(
      scheduleDocData,
      bookingDentistId
    );
    if (rangeOverlapsScheduleUnavailable(newStartMin, newEndMin, bookingUnavail)) {
      return {
        ok: false,
        message:
          "Booking failed. The selected services would push the appointment past the dentist's scheduled working hours.",
        pushUpdates: [],
      };
    }
  }

  const active = (Array.isArray(appointments) ? appointments : []).filter((appt) => {
    const status = (appt?.status || "").toLowerCase();
    return status !== "cancelled" && status !== "canceled";
  });

  const plottedChair = bookingDentistId
    ? getDentistScheduleEntry(scheduleDocData, bookingDentistId)?.chairNumber ?? null
    : null;

  const chairTryOrder = [];
  const addChair = (n) => {
    const c = Number(n);
    if (!Number.isFinite(c) || c < 1 || c > cc) return;
    if (!chairTryOrder.includes(c)) chairTryOrder.push(c);
  };
  addChair(plottedChair);
  for (let i = 1; i <= cc; i += 1) addChair(i);

  const timeslotMessage =
    "Booking failed. The total duration of the selected services exceeds the available timeslot.";
  const immovableBlockMessage =
    "Booking failed. The selected services overlap with confirmed/completed appointments that cannot be moved.";

  const chairTable = assignAppointmentsToChairsForBooking({
    appointments: active,
    chairCount: cc,
    scheduleDocData,
  });

  // Prefer immediate placement on any currently free chair before attempting
  // pending-chain pushes on a preferred chair.
  for (const targetChair of chairTryOrder) {
    if (!isChairOccupiedAtRange(chairTable, targetChair, newStartMin, newEndMin)) {
      return { ok: true, message: "", pushUpdates: [] };
    }
  }

  for (const targetChair of chairTryOrder) {
    const bucket = chairTable.find((entry) => entry.chairNum === targetChair);
    const onChair = Array.isArray(bucket?.appointments) ? bucket.appointments : [];

    const hardOnChair = onChair.filter(
      (appt) =>
        appointmentOverlapsRange(appt, newStartMin, newEndMin) &&
        isHardChairBlockStatus(appt.status)
    );
    if (hardOnChair.length > 0) continue;

    const pendingOnChair = onChair
      .map((appt) => ({
        appt,
        range: getAppointmentRangeMinutesForChair(appt),
      }))
      .filter(
        (row) =>
          row.range &&
          row.range.start >= newStartMin &&
          isCompressiblePendingStatus(row.appt.status)
      )
      .sort((a, b) => a.range.start - b.range.start);

    let pushCursor = newEndMin;
    const pushUpdates = [];

    let pushValid = true;
    for (const { appt, range } of pendingOnChair) {
      if (range.start >= pushCursor) break;
      const dur = range.end - range.start;
      const shiftedStart = pushCursor;
      const shiftedEnd = shiftedStart + dur;
      if (shiftedEnd > LAST_MINUTE_OF_CALENDAR_DAY) {
        pushValid = false;
        break;
      }
      const pendingDentistUid = resolveAppointmentDentistUid(appt);
      const pendingUnavail = pendingDentistUid
        ? getDentistScheduleUnavailableRanges(scheduleDocData, pendingDentistUid)
        : null;
      if (rangeOverlapsScheduleUnavailable(shiftedStart, shiftedEnd, pendingUnavail)) {
        pushValid = false;
        break;
      }
      const apptId = appt.id || appt.appointmentId;
      const origStart = appt.startTime || appt.time || "";
      const origEnd = appt.endTime || "";
      const nextStart = formatClock12FromMinutes(shiftedStart);
      const nextEnd = formatClock12FromMinutes(shiftedEnd);
      if (apptId && (origStart !== nextStart || origEnd !== nextEnd)) {
        pushUpdates.push({
          id: apptId,
          startTime: nextStart,
          endTime: nextEnd,
          originalStart: formatClock12FromMinutes(range.start),
          originalEnd: formatClock12FromMinutes(range.end),
        });
      }
      pushCursor = shiftedEnd;
    }
    if (!pushValid) continue;

    // Cross-chair same-dentist conflict: reject this chair if any pushed appointment's
    // new time would overlap a fixed (non-pushed) appointment from the same dentist on
    // another chair. (Mirrors the same check in simulateEditChairCascade.)
    const pushedIds = new Set(pushUpdates.map((u) => u.id));
    let hasCrossChairDentistConflict = false;
    for (const pushRow of pushUpdates) {
      const pushedAppt = active.find((a) => (a.id || a.appointmentId) === pushRow.id);
      if (!pushedAppt) continue;
      const dentistUid = resolveAppointmentDentistUid(pushedAppt);
      if (!dentistUid) continue;
      const shiftedStart = parseClockLabelToMinutes(pushRow.startTime);
      const shiftedEnd = parseClockLabelToMinutes(pushRow.endTime);
      if (!Number.isFinite(shiftedStart) || !Number.isFinite(shiftedEnd)) continue;
      for (const fixedAppt of active) {
        const fixedId = fixedAppt.id || fixedAppt.appointmentId;
        if (!fixedId || pushedIds.has(fixedId)) continue;
        if (resolveAppointmentDentistUid(fixedAppt) !== dentistUid) continue;
        const fixedRange = getAppointmentDisplayRangeMinutes(fixedAppt);
        if (!fixedRange) continue;
        if (shiftedStart < fixedRange.end && shiftedEnd > fixedRange.start) {
          hasCrossChairDentistConflict = true;
          break;
        }
      }
      if (hasCrossChairDentistConflict) break;
    }
    if (hasCrossChairDentistConflict) continue;

    const simulatedAppointments = active.map((appt) => {
      const id = appt.id || appt.appointmentId;
      const row = pushUpdates.find((u) => u.id === id);
      if (!row) return appt;
      const shiftedStart = parseClockLabelToMinutes(row.startTime);
      const shiftedEnd = parseClockLabelToMinutes(row.endTime);
      return cloneAppointmentWithMinuteRange(appt, shiftedStart, shiftedEnd);
    });

    const tableAfterPush = assignAppointmentsToChairsForBooking({
      appointments: simulatedAppointments,
      chairCount: cc,
      scheduleDocData,
    });

    // Reject push chains that force a shifted pending appointment into a window
    // where all chairs are hard-blocked (confirmed/completed/reserved).
    const hasHardBlockedShift = pushUpdates.some((row) => {
      const shiftedStart = parseClockLabelToMinutes(row.startTime);
      const shiftedEnd = parseClockLabelToMinutes(row.endTime);
      if (!Number.isFinite(shiftedStart) || !Number.isFinite(shiftedEnd) || shiftedEnd <= shiftedStart) {
        return true;
      }
      return getChairBlockSeverityAtRange(tableAfterPush, shiftedStart, shiftedEnd) === "chair-full";
    });
    if (hasHardBlockedShift) {
      return { ok: false, message: immovableBlockMessage, pushUpdates: [] };
    }

    if (!isChairOccupiedAtRange(tableAfterPush, targetChair, newStartMin, newEndMin)) {
      return { ok: true, message: "", pushUpdates };
    }
  }

  return { ok: false, message: timeslotMessage, pushUpdates: [] };
}

export function validatePatientBookingDurationOnChairPlot(params = {}) {
  const { ok, message } = planPatientBookingOnChairPlot(params);
  return { ok, message: ok ? "" : message };
}

/** True when every chair has an appointment overlapping [startMin, endMin). */
export function areAllChairsOccupiedAtRange(chairTableData, startMin, endMin) {
  if (!Array.isArray(chairTableData) || chairTableData.length === 0) return false;
  return chairTableData.every(({ appointments }) =>
    (Array.isArray(appointments) ? appointments : []).some((appt) =>
      appointmentOverlapsRange(appt, startMin, endMin)
    )
  );
}

/** True when at least one chair has no appointment overlapping [startMin, endMin). */
export function isAnyChairFreeAtRange(chairTableData, startMin, endMin) {
  const table = Array.isArray(chairTableData) ? chairTableData : [];
  if (table.length === 0) return true;
  return table.some(
    ({ chairNum }) => !isChairOccupiedAtRange(chairTableData, chairNum, startMin, endMin)
  );
}

/** True when the given chair has an appointment overlapping [startMin, endMin). */
export function isChairOccupiedAtRange(chairTableData, chairNum, startMin, endMin) {
  const chair = Number(chairNum);
  if (!Number.isFinite(chair) || chair <= 0) return false;
  const bucket = (Array.isArray(chairTableData) ? chairTableData : []).find(
    (entry) => entry.chairNum === chair
  );
  if (!bucket) return false;
  return (Array.isArray(bucket.appointments) ? bucket.appointments : []).some((appt) =>
    appointmentOverlapsRange(appt, startMin, endMin)
  );
}

/**
 * How many distinct chairs have at least one non-cancelled appointment on a date.
 * Uses the same placement rules as patient booking (plotted chair first, then first free).
 */
export function countChairsWithPatientAppointments({
  appointments = [],
  dateKey,
  chairCount,
  scheduleDocData = null,
} = {}) {
  const cc = Number(chairCount);
  if (!dateKey || !Number.isFinite(cc) || cc <= 0) return 0;

  const active = (Array.isArray(appointments) ? appointments : []).filter((appt) => {
    if (!appt || appt.date !== dateKey) return false;
    const status = (appt.status || "").toLowerCase();
    return status !== "cancelled" && status !== "canceled";
  });
  if (active.length === 0) return 0;

  const chairTable = assignAppointmentsToChairsForBooking({
    appointments: active,
    chairCount: cc,
    scheduleDocData,
  });

  return chairTable.filter(
    ({ appointments: chairAppts }) =>
      Array.isArray(chairAppts) && chairAppts.length > 0
  ).length;
}

/** Resolve chair count for a date from a chairSchedulesByDate document snapshot. */
export function resolveChairCountForDateKey(
  dateKey,
  chairSchedulesDocData = null,
  globalSettings = null,
  maxChairs = null
) {
  const count = buildChairSchedulesMapFromDocData(chairSchedulesDocData).get(dateKey);
  const dateChairSchedule =
    Number.isFinite(count) && count > 0 ? { chairCount: count } : null;
  return resolveChairCountForDate({
    dateChairSchedule,
    globalSettings,
    maxChairs,
  });
}

/**
 * When editing a pending/new visit, returns a confirmed/completed appointment on the
 * same physical chair column that blocks the proposed time range, or null if allowed.
 */
export function findPendingEditImmovableConflict({
  editingAppointmentId,
  editingDentistId: _editingDentistId,
  newStartMin,
  newEndMin,
  allDayAppointments = [],
  scheduleDocData = null,
  chairCount = 0,
} = {}) {
  const immovableStatuses = new Set(["confirmed", "completed"]);

  const active = (Array.isArray(allDayAppointments) ? allDayAppointments : []).filter(
    (appt) => {
      const st = (appt?.status || "").toLowerCase();
      return st !== "cancelled" && st !== "canceled";
    }
  );

  const applyEditToRow = (appt) => {
    if (!appt || !appointmentMatchesId(appt, editingAppointmentId)) return appt;
    const duration = Math.max(1, newEndMin - newStartMin);
    const startLabel = formatClock12FromMinutes(newStartMin);
    const endLabel = formatClock12FromMinutes(newEndMin);
    return {
      ...appt,
      startTime: startLabel,
      time: startLabel,
      endTime: endLabel,
      duration,
    };
  };

  const updatedActive = active.map(applyEditToRow);

  const findTimeOverlappingImmovable = (pool) =>
    (Array.isArray(pool) ? pool : []).filter((appt) => {
      if (!appt || appointmentMatchesId(appt, editingAppointmentId)) return false;
      const status = (appt.status || "").toLowerCase();
      if (!immovableStatuses.has(status)) return false;
      const range = getAppointmentRangeMinutesForChair(appt);
      if (!range) return false;
      return newStartMin < range.end && range.start < newEndMin;
    });

  const overlapping = findTimeOverlappingImmovable(active);
  if (overlapping.length === 0) return null;

  const cc = Number(chairCount);
  if (!cc || cc <= 0 || !scheduleDocData) {
    return overlapping.sort(
      (a, b) =>
        (getAppointmentRangeMinutesForChair(a)?.start ?? 0) -
        (getAppointmentRangeMinutesForChair(b)?.start ?? 0)
    )[0];
  }

  const scheduleBefore = buildScheduleWithDisplayChairs(scheduleDocData, active, cc);
  const scheduleAfter = buildScheduleWithDisplayChairs(scheduleDocData, updatedActive, cc);
  const chairTableBefore = assignAppointmentsToChairsForBooking({
    appointments: active,
    chairCount: cc,
    scheduleDocData: scheduleBefore,
  });
  const chairTableAfter = assignAppointmentsToChairsForBooking({
    appointments: updatedActive,
    chairCount: cc,
    scheduleDocData: scheduleAfter,
  });

  const chairsToCheck = new Set();
  const beforeChair = findChairNumForAppointment(chairTableBefore, editingAppointmentId);
  const afterChair = findChairNumForAppointment(chairTableAfter, editingAppointmentId);
  // Only check the chair the editing appointment occupies after the edit.
  // If it moved to a different chair, confirmed appointments on the before-chair
  // are on a different physical column and cannot block the new range.
  if (afterChair != null) {
    chairsToCheck.add(afterChair);
  } else if (beforeChair != null) {
    chairsToCheck.add(beforeChair);
  }

  for (const chairNum of chairsToCheck) {
    const bucketAppts = getChairAppointmentsFromTable(chairTableAfter, chairNum);
    const blockers = bucketAppts
      .filter((other) => {
        if (!other || appointmentMatchesId(other, editingAppointmentId)) return false;
        const status = (other.status || "").toLowerCase();
        if (!immovableStatuses.has(status)) return false;
        const range = getAppointmentRangeMinutesForChair(other);
        if (!range) return false;
        return newStartMin < range.end && range.start < newEndMin;
      })
      .sort(
        (a, b) =>
          (getAppointmentRangeMinutesForChair(a)?.start ?? 0) -
          (getAppointmentRangeMinutesForChair(b)?.start ?? 0)
      );
    if (blockers.length > 0) return blockers[0];
  }

  return null;
}

function rangesOverlapMinutes(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

function rangeExceedsPlottedSchedule(scheduleDocData, dentistUid, startMin, endMin) {
  if (!dentistUid || !scheduleDocData) return false;
  if (!getDentistScheduleEntry(scheduleDocData, dentistUid)) return false;
  return !isRangeWithinPlottedSchedule(scheduleDocData, dentistUid, startMin, endMin);
}

/** Contiguous plotted windows (adjacent half-hour tiles merge, e.g. 9:00–12:00). */
export function getMergedPlottedScheduleRangesForDentist(scheduleDocData, dentistUid) {
  const entry = getDentistScheduleEntry(scheduleDocData, dentistUid);
  if (!entry) return [];
  const sorted = entry.availableSlots
    .map(parsePlottedSlotLabelRange)
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);
  if (sorted.length === 0) return [];

  return sorted.reduce((acc, next) => {
    if (acc.length === 0) {
      acc.push({ start: next.start, end: next.end });
      return acc;
    }
    const last = acc[acc.length - 1];
    if (next.start <= last.end) {
      last.end = Math.max(last.end, next.end);
    } else {
      acc.push({ start: next.start, end: next.end });
    }
    return acc;
  }, []);
}

/**
 * Requested edit overlaps a confirmed/completed visit on the same chair (not compressible).
 * @returns {{ dentistUid: string, reason: string } | null}
 */
function findSameChairImmovableOverlapViolation(
  chairAppts,
  editingAppointmentId,
  newStartMin,
  newEndMin
) {
  for (const other of chairAppts) {
    if (appointmentMatchesId(other, editingAppointmentId)) continue;
    if (isCompressiblePendingStatus(other.status)) continue;

    const otherRange = getAppointmentRangeMinutesForChair(other);
    if (!otherRange) continue;

    if (!rangesOverlapMinutes(newStartMin, newEndMin, otherRange.start, otherRange.end)) {
      continue;
    }

    const otherUid = resolveAppointmentDentistUid(other);
    if (otherUid) {
      return { dentistUid: otherUid, reason: "same_chair_overlap", blockerAppt: other };
    }
  }
  return null;
}

const IMMOVABLE_CHAIR_STATUSES = new Set(["confirmed", "completed"]);

/** Confirmed/completed on this chair that overlaps [startMin, endMin). */
function findChairImmovableRangeOverlapBlocker(
  chairAppts,
  startMin,
  endMin,
  editingAppointmentId
) {
  let earliest = null;
  let earliestStart = Infinity;

  for (const anchor of Array.isArray(chairAppts) ? chairAppts : []) {
    if (!anchor || appointmentMatchesId(anchor, editingAppointmentId)) continue;
    const st = (anchor.status || "").toLowerCase();
    if (!IMMOVABLE_CHAIR_STATUSES.has(st)) continue;
    const range = getAppointmentRangeMinutesForChair(anchor);
    if (!range) continue;
    if (!rangesOverlapMinutes(startMin, endMin, range.start, range.end)) continue;
    if (range.start < earliestStart) {
      earliestStart = range.start;
      earliest = anchor;
    }
  }

  if (!earliest) return null;
  return {
    dentistUid: resolveAppointmentDentistUid(earliest) || "",
    reason: "same_chair_overlap",
    blockerAppt: earliest,
  };
}

function resolveCompressedFinalRangeMinutes(appt, compressionUpdates) {
  const update = (compressionUpdates || []).find((row) =>
    appointmentMatchesId({ id: row.id }, appt?.id || appt?.appointmentId)
  );
  if (update) {
    const start = parseClockLabelToMinutes(update.startTime);
    const end = parseClockLabelToMinutes(update.endTime);
    if (Number.isFinite(start) && Number.isFinite(end) && end > start) {
      return { start, end };
    }
  }
  const range = getAppointmentDisplayRangeMinutes(appt);
  if (!range || !Number.isFinite(range.start) || !Number.isFinite(range.end)) {
    return null;
  }
  return { start: range.start, end: range.end };
}

function sortChairAppointmentsByStart(chairAppts) {
  return [...chairAppts].sort((a, b) => {
    const aRow = getAppointmentDisplayRangeMinutes(a);
    const bRow = getAppointmentDisplayRangeMinutes(b);
    return (aRow?.start ?? 0) - (bRow?.start ?? 0);
  });
}

function resolveEditedOrigEndMin(appt, origStartMin) {
  if (!Number.isFinite(origStartMin)) return origStartMin + 30;
  const dur = Math.max(1, Number(appt?.duration) || 30);
  const range = getAppointmentDisplayRangeMinutes(appt);
  if (range && range.start === origStartMin && range.end > range.start) {
    return range.end;
  }
  if (appt?.endTime) {
    const parsedEnd = parseClockLabelToMinutes(appt.endTime);
    const parsedStart = parseClockLabelToMinutes(appt.startTime || appt.time);
    if (
      Number.isFinite(parsedEnd) &&
      Number.isFinite(parsedStart) &&
      parsedStart === origStartMin
    ) {
      return parsedEnd;
    }
  }
  return origStartMin + dur;
}

/**
 * Mirror ManageAppointment backward save: shift predecessors by δ (minus slack), then compress.
 * Cascade order uses the edited appointment's original start (not the proposed earlier start)
 * so predecessors are not missed when the new start ties with them on the clock.
 */
function buildChairEditCascadeSyntheticSorted(
  chairAppts,
  editingAppointmentId,
  newStartMin,
  newEndMin,
  origStartMin
) {
  const applyEditRow = (appt) => {
    const duration = Math.max(1, newEndMin - newStartMin);
    return {
      ...appt,
      startTime: formatClock12FromMinutes(newStartMin),
      time: formatClock12FromMinutes(newStartMin),
      endTime: formatClock12FromMinutes(newEndMin),
      duration,
    };
  };

  const orderSorted = sortChairAppointmentsByStart(
    chairAppts.map((ap) => {
      if (!appointmentMatchesId(ap, editingAppointmentId)) return ap;
      const origEndMin = resolveEditedOrigEndMin(ap, origStartMin);
      return {
        ...ap,
        startTime: formatClock12FromMinutes(origStartMin),
        time: formatClock12FromMinutes(origStartMin),
        endTime: formatClock12FromMinutes(origEndMin),
        duration: Math.max(1, origEndMin - origStartMin),
      };
    })
  );

  const cascadeIdx = orderSorted.findIndex((ap) =>
    appointmentMatchesId(ap, editingAppointmentId)
  );
  if (cascadeIdx === -1) {
    const sorted = sortChairAppointmentsByStart(chairAppts);
    return { syntheticSorted: sorted, cascadeIdx: -1, deltaEarlier: 0, isBackward: false };
  }

  if (!(newStartMin < origStartMin)) {
    const sorted = sortChairAppointmentsByStart(chairAppts);
    return {
      syntheticSorted: sorted.map((ap) =>
        appointmentMatchesId(ap, editingAppointmentId) ? applyEditRow(ap) : ap
      ),
      cascadeIdx,
      deltaEarlier: 0,
      isBackward: false,
    };
  }

  let slackMinutesBeforeEdited = 0;
  if (cascadeIdx > 0) {
    const prevRange = getAppointmentDisplayRangeMinutes(orderSorted[cascadeIdx - 1]);
    if (prevRange) {
      slackMinutesBeforeEdited = Math.max(0, origStartMin - prevRange.end);
    }
  }
  const requestedEarlierMinutes = Math.max(0, origStartMin - newStartMin);
  const deltaEarlier =
    cascadeIdx > 0
      ? Math.max(0, requestedEarlierMinutes - slackMinutesBeforeEdited)
      : requestedEarlierMinutes;

  const orderIndexById = new Map();
  orderSorted.forEach((ap, j) => {
    const id = ap.id || ap.appointmentId;
    if (id) orderIndexById.set(id, j);
  });

  const syntheticSorted = sortChairAppointmentsByStart(
    chairAppts.map((ap) => {
      if (appointmentMatchesId(ap, editingAppointmentId)) {
        return applyEditRow(ap);
      }
      const orderIdx = orderIndexById.get(ap.id || ap.appointmentId);
      if (
        orderIdx != null &&
        orderIdx < cascadeIdx &&
        isCompressiblePendingStatus(ap.status) &&
        deltaEarlier > 0
      ) {
        const range = getAppointmentDisplayRangeMinutes(ap);
        if (!range) return ap;
        const ns = range.start - deltaEarlier;
        const ne = range.end - deltaEarlier;
        return {
          ...ap,
          startTime: formatClock12FromMinutes(ns),
          time: formatClock12FromMinutes(ns),
          endTime: formatClock12FromMinutes(ne),
          duration: ne - ns,
        };
      }
      return ap;
    })
  );

  return {
    syntheticSorted,
    cascadeIdx,
    deltaEarlier,
    isBackward: true,
  };
}

function buildScheduleWithDisplayChairs(
  plotData,
  appointmentsForDate,
  chairCount,
  options = {}
) {
  const displayChairs = getDisplayChairNumbersForDate(plotData, {
    appointmentsForDate,
    chairCount,
    ...options,
  });
  const scheduleWithDisplay = { ...plotData };
  Object.entries(displayChairs).forEach(([uid, chairNum]) => {
    if (!scheduleWithDisplay[uid] || typeof scheduleWithDisplay[uid] !== "object") return;
    scheduleWithDisplay[uid] = { ...scheduleWithDisplay[uid], chairNumber: chairNum };
  });
  return scheduleWithDisplay;
}

function findChairNumForAppointment(chairTable, editingAppointmentId) {
  for (const bucket of Array.isArray(chairTable) ? chairTable : []) {
    const hasEdit = (bucket.appointments || []).some((a) =>
      appointmentMatchesId(a, editingAppointmentId)
    );
    if (hasEdit) return bucket.chairNum;
  }
  return null;
}

function getChairAppointmentsFromTable(chairTable, chairNum) {
  const bucket = (Array.isArray(chairTable) ? chairTable : []).find(
    (entry) => entry.chairNum === chairNum
  );
  return Array.isArray(bucket?.appointments) ? bucket.appointments : [];
}

/**
 * Chair columns for cascade — same placement rules as the staff dashboard timetable.
 */
function buildDisplayChairTable(appointments, scheduleWithDisplay, chairCount) {
  const cc = Number(chairCount);
  if (!Number.isFinite(cc) || cc <= 0) return [];

  const active = (Array.isArray(appointments) ? appointments : []).filter((appt) => {
    const st = (appt?.status || "").toLowerCase();
    return st !== "cancelled" && st !== "canceled";
  });

  return assignAppointmentsToChairsForBooking({
    appointments: active,
    chairCount: cc,
    scheduleDocData: scheduleWithDisplay,
  });
}

/**
 * Chairs to simulate, in priority order (where this appointment sits in the timetable first).
 */
function buildCascadeChairEvaluationPlan({
  assignChairTableBefore,
  assignChairTableAfter,
  editingAppointmentId,
}) {
  const plan = [];
  const seen = new Set();

  const push = (chairNum, mode) => {
    const c = Number(chairNum);
    if (!Number.isFinite(c) || c <= 0 || seen.has(`${c}:${mode}`)) return;
    seen.add(`${c}:${mode}`);
    plan.push({ chairNum: c, mode });
  };

  const assignBefore = findChairNumForAppointment(
    assignChairTableBefore,
    editingAppointmentId
  );
  const assignAfter = findChairNumForAppointment(
    assignChairTableAfter,
    editingAppointmentId
  );

  // Validate the physical chair column where the visit is shown before any reassignment dodge.
  if (assignBefore != null) push(assignBefore, "assign_before");
  if (assignAfter != null && assignAfter !== assignBefore) push(assignAfter, "assign_after");

  return { plan, assignBefore, assignAfter };
}

function resolveChairApptsForCascadeEvaluation({
  chairNum,
  mode,
  assignChairTableBefore,
  assignChairTableAfter,
  applyEditToRow,
}) {
  if (mode === "assign_before") {
    return getChairAppointmentsFromTable(assignChairTableBefore, chairNum)
      .map((ap) => applyEditToRow(ap))
      .filter((ap) => {
        // Exclude appointments naturally reassigned to a different chair in the after-table.
        // They are independently validated by the booking-reassignment loop; keeping them here
        // would cascade them on their original chair and produce a false conflict.
        const apId = ap.appointmentId || ap.id || "";
        if (!apId) return true;
        const afterChair = findChairNumForAppointment(assignChairTableAfter, apId);
        return afterChair == null || afterChair === chairNum;
      });
  }
  return getChairAppointmentsFromTable(assignChairTableAfter, chairNum);
}

/**
 * Run backward-then-forward cascade for one chair bucket; return first plot/day violation.
 */
function evaluateChairBucketCascadeViolation({
  chairAppts,
  editingAppointmentId,
  newStartMin,
  newEndMin,
  origStart,
  plotData,
  editingDentistId,
  skipDirectImmovableCheck = false,
}) {
  if (!Array.isArray(chairAppts) || chairAppts.length === 0) return null;

  if (!skipDirectImmovableCheck) {
    const directImmovableBlock = findChairImmovableRangeOverlapBlocker(
      chairAppts,
      newStartMin,
      newEndMin,
      editingAppointmentId
    );
    if (directImmovableBlock) return directImmovableBlock;
  }

  const violationForShiftedPending = (otherAppt, shiftedStart, shiftedEnd) => {
    const otherUid = resolveAppointmentDentistUid(otherAppt);
    if (!otherUid || !getDentistScheduleEntry(plotData, otherUid)) return null;
    if (!rangeExceedsPlottedSchedule(plotData, otherUid, shiftedStart, shiftedEnd)) {
      return null;
    }
    return { dentistUid: otherUid, reason: "beyond_plot" };
  };

  const { syntheticSorted, deltaEarlier, isBackward } = buildChairEditCascadeSyntheticSorted(
    chairAppts,
    editingAppointmentId,
    newStartMin,
    newEndMin,
    origStart
  );

  const editIdxInSynthetic = syntheticSorted.findIndex((ap) =>
    appointmentMatchesId(ap, editingAppointmentId)
  );

  // Only check for backward cascade conflicts when the appointment was actually moved earlier.
  // When isBackward=false no predecessors were shifted, so every confirmed/completed appointment
  // would simply find itself overlapping its own position — a false positive.
  if (isBackward) {
    for (const row of syntheticSorted) {
      if (appointmentMatchesId(row, editingAppointmentId)) continue;
      // Confirmed/completed appointments are never shifted backward — their position
      // in syntheticSorted equals their original position, so they would self-detect
      // as immovable blockers via findChairImmovableRangeOverlapBlocker (false positive).
      const rowSt = (row.status || "").toLowerCase();
      if (IMMOVABLE_CHAIR_STATUSES.has(rowSt)) continue;
      const rowRange = getAppointmentDisplayRangeMinutes(row);
      if (!rowRange) continue;
      const immovableBlock = findChairImmovableRangeOverlapBlocker(
        chairAppts,
        rowRange.start,
        rowRange.end,
        editingAppointmentId
      );
      if (immovableBlock) {
        return { ...immovableBlock, reason: "backward_cascade" };
      }
    }
  }

  if (deltaEarlier > 0 && editIdxInSynthetic > 0) {
    for (let pj = 0; pj < editIdxInSynthetic; pj += 1) {
      const predRow = syntheticSorted[pj];
      if (!predRow || !isCompressiblePendingStatus(predRow.status)) continue;
      const preRange = getAppointmentDisplayRangeMinutes(predRow);
      if (!preRange) continue;
      const backwardViolation = violationForShiftedPending(
        predRow,
        preRange.start,
        preRange.end
      );
      if (backwardViolation) {
        return { ...backwardViolation, reason: "backward_cascade" };
      }
    }
  }

  const { updates, exceedsDayEnd } = computeCompressibleTimelineShiftUpdates(
    syntheticSorted,
    {
      isCompressible: (_apt, st) => isCompressiblePendingStatus(st),
    }
  );

  if (exceedsDayEnd) {
    return {
      dentistUid:
        editingDentistId || resolveAppointmentDentistUid(syntheticSorted[0]) || "",
      reason: "day_end",
    };
  }

  for (const appt of syntheticSorted) {
    // Confirmed/completed appointments are never compressible — they never appear in
    // updates and their finalRange always equals their original position, which would
    // cause them to self-detect as immovable blockers. Skip them here; the direct
    // immovable check at the top of this function already covers the editing appt's
    // new range conflicting with any confirmed/completed appointment.
    const apptSt = (appt.status || "").toLowerCase();
    if (IMMOVABLE_CHAIR_STATUSES.has(apptSt)) continue;
    const finalRange = resolveCompressedFinalRangeMinutes(appt, updates);
    if (!finalRange) continue;
    const immovableBlock = findChairImmovableRangeOverlapBlocker(
      chairAppts,
      finalRange.start,
      finalRange.end,
      editingAppointmentId
    );
    if (immovableBlock && !appointmentMatchesId(appt, editingAppointmentId)) {
      return { ...immovableBlock, reason: "forward_cascade" };
    }
  }

  const plotViolations = [];
  for (const appt of syntheticSorted) {
    const finalRange = resolveCompressedFinalRangeMinutes(appt, updates);
    if (!finalRange) continue;
    const plotViolation = violationForShiftedPending(
      appt,
      finalRange.start,
      finalRange.end
    );
    if (!plotViolation) continue;

    let reason = "forward_cascade";
    if (appointmentMatchesId(appt, editingAppointmentId)) {
      reason = "beyond_plot";
    } else if (isBackward && finalRange.start < origStart) {
      reason = "backward_cascade";
    }
    plotViolations.push({ appt, plotViolation, reason });
  }

  if (plotViolations.length > 0) {
    for (const { appt } of plotViolations) {
      if (appointmentMatchesId(appt, editingAppointmentId)) continue;
      const row = getAppointmentDisplayRangeMinutes(appt);
      if (!row) continue;
      const immovableBlock = findChairImmovableRangeOverlapBlocker(
        chairAppts,
        row.start,
        row.end,
        editingAppointmentId
      );
      if (immovableBlock) {
        return { ...immovableBlock, reason: "backward_cascade" };
      }
    }

    const backwardOther = plotViolations.find(
      ({ appt, reason }) =>
        reason === "backward_cascade" &&
        !appointmentMatchesId(appt, editingAppointmentId)
    );
    const overlappingOther = plotViolations.find(({ appt }) => {
      if (appointmentMatchesId(appt, editingAppointmentId)) return false;
      const row = getAppointmentDisplayRangeMinutes(appt);
      return row && newStartMin < row.end && row.start < newEndMin;
    });
    const otherOnChair = plotViolations.find(
      ({ appt }) => !appointmentMatchesId(appt, editingAppointmentId)
    );
    const pick = backwardOther || overlappingOther || otherOnChair || plotViolations[0];
    return { ...pick.plotViolation, reason: pick.reason };
  }

  if (!isBackward) {
    const editedUpdate = updates.find((u) =>
      appointmentMatchesId({ id: u.id }, editingAppointmentId)
    );
    if (editedUpdate) {
      const compressedStart = parseClockLabelToMinutes(editedUpdate.startTime);
      if (Number.isFinite(compressedStart) && compressedStart !== newStartMin) {
        const immovableOverlap = findSameChairImmovableOverlapViolation(
          chairAppts,
          editingAppointmentId,
          newStartMin,
          newEndMin
        );
        if (immovableOverlap) return immovableOverlap;
        const blockerUid =
          resolveAppointmentDentistUid(
            chairAppts.find((a) => !appointmentMatchesId(a, editingAppointmentId))
          ) || editingDentistId;
        if (blockerUid) {
          return { dentistUid: blockerUid, reason: "same_chair_overlap" };
        }
      }
    }
  } else {
    const editedAppt = syntheticSorted.find((a) =>
      appointmentMatchesId(a, editingAppointmentId)
    );
    const editedFinal = editedAppt
      ? resolveCompressedFinalRangeMinutes(editedAppt, updates)
      : null;
    if (editedFinal && editedFinal.start !== newStartMin) {
      const immovableOverlap = findSameChairImmovableOverlapViolation(
        chairAppts,
        editingAppointmentId,
        newStartMin,
        newEndMin
      );
      if (immovableOverlap) return immovableOverlap;
      const blockerUid =
        resolveAppointmentDentistUid(
          chairAppts.find((a) => !appointmentMatchesId(a, editingAppointmentId))
        ) || editingDentistId;
      if (blockerUid) {
        return { dentistUid: blockerUid, reason: "same_chair_overlap" };
      }
    }
  }

  return null;
}

/** Collect timeline updates after cascade on one chair bucket (for staff save). */
function collectChairBucketCascadeUpdates({
  chairAppts,
  editingAppointmentId,
  newStartMin,
  newEndMin,
  origStart,
}) {
  const built = buildChairEditCascadeSyntheticSorted(
    chairAppts,
    editingAppointmentId,
    newStartMin,
    newEndMin,
    origStart
  );
  const { updates, exceedsDayEnd } = computeCompressibleTimelineShiftUpdates(
    built.syntheticSorted,
    {
      isCompressible: (_apt, st) => isCompressiblePendingStatus(st),
    }
  );

  if (exceedsDayEnd) {
    return { exceedsDayEnd: true, updatesById: new Map() };
  }

  const syntheticSorted = built.syntheticSorted;

  const updatesById = new Map();
  for (const appt of syntheticSorted) {
    const finalRange = resolveCompressedFinalRangeMinutes(appt, updates);
    if (!finalRange) continue;
    const id = appt.id || appt.appointmentId;
    if (!id) continue;
    const startTime = formatClock12FromMinutes(finalRange.start);
    const endTime = formatClock12FromMinutes(finalRange.end);
    const origRange = getAppointmentDisplayRangeMinutes(appt);
    if (
      origRange &&
      origRange.start === finalRange.start &&
      origRange.end === finalRange.end
    ) {
      continue;
    }
    updatesById.set(id, {
      startTime,
      endTime,
      duration: Math.max(1, finalRange.end - finalRange.start),
    });
  }
  return { exceedsDayEnd: false, updatesById };
}

/**
 * Shared chair-cascade simulation for edit validation and staff save.
 * Validates original-chair timeline (prevents dodging cross-dentist violations via reassignment)
 * and post-edit chair assignment; returns Firestore-ready time updates when valid.
 */
export function simulateEditChairCascade({
  editingAppointmentId,
  editingDentistId = null,
  newStartMin,
  newEndMin,
  originalStartMin = null,
  allDayAppointments = [],
  scheduleDocData = null,
  chairCount = 0,
} = {}) {
  const cc = Number(chairCount);
  const empty = { violation: null, updatesById: new Map() };
  if (!cc || cc <= 0 || !editingAppointmentId) return empty;
  if (
    !Number.isFinite(newStartMin) ||
    !Number.isFinite(newEndMin) ||
    newEndMin <= newStartMin
  ) {
    return empty;
  }

  const plotData =
    scheduleDocData && typeof scheduleDocData === "object" ? scheduleDocData : {};

  const active = (Array.isArray(allDayAppointments) ? allDayAppointments : []).filter(
    (appt) => {
      const st = (appt?.status || "").toLowerCase();
      return st !== "cancelled" && st !== "canceled";
    }
  );

  const applyEditToRow = (appt) => {
    if (!appt || !appointmentMatchesId(appt, editingAppointmentId)) return appt;
    const duration = Math.max(1, newEndMin - newStartMin);
    const startLabel = formatClock12FromMinutes(newStartMin);
    const endLabel = formatClock12FromMinutes(newEndMin);
    return {
      ...appt,
      startTime: startLabel,
      time: startLabel,
      endTime: endLabel,
      duration,
    };
  };

  const updatedActive = active.map(applyEditToRow);
  const origStart =
    typeof originalStartMin === "number" && !Number.isNaN(originalStartMin)
      ? originalStartMin
      : newStartMin;

  const scheduleBefore = buildScheduleWithDisplayChairs(plotData, active, cc);
  const chairTableBefore = assignAppointmentsToChairsForBooking({
    appointments: active,
    chairCount: cc,
    scheduleDocData: scheduleBefore,
  });
  const displayChairTableBefore = buildDisplayChairTable(active, scheduleBefore, cc);

  const scheduleAfter = buildScheduleWithDisplayChairs(plotData, updatedActive, cc);
  const chairTableAfter = assignAppointmentsToChairsForBooking({
    appointments: updatedActive,
    chairCount: cc,
    scheduleDocData: scheduleAfter,
  });
  const displayChairTableAfter = buildDisplayChairTable(updatedActive, scheduleAfter, cc);

  const { plan, assignAfter: afterChairNum } = buildCascadeChairEvaluationPlan({
    assignChairTableBefore: chairTableBefore,
    assignChairTableAfter: chairTableAfter,
    editingAppointmentId,
  });

  for (const { chairNum, mode } of plan) {
    const chairAppts = resolveChairApptsForCascadeEvaluation({
      chairNum,
      mode,
      assignChairTableBefore: chairTableBefore,
      assignChairTableAfter: chairTableAfter,
      applyEditToRow,
    });

    const violation = evaluateChairBucketCascadeViolation({
      chairAppts,
      editingAppointmentId,
      newStartMin,
      newEndMin,
      origStart,
      plotData,
      editingDentistId,
      // Editing appointment is leaving this chair — its new range belongs on the
      // destination chair, so don't check it against this chair's confirmed appointments.
      skipDirectImmovableCheck:
        mode === "assign_before" &&
        afterChairNum != null &&
        afterChairNum !== chairNum,
    });
    if (violation) {
      return { violation, updatesById: new Map() };
    }
  }

  const violationForShiftedPending = (otherAppt, shiftedStart, shiftedEnd) => {
    const otherUid = resolveAppointmentDentistUid(otherAppt);
    if (!otherUid || !getDentistScheduleEntry(plotData, otherUid)) return null;
    if (!rangeExceedsPlottedSchedule(plotData, otherUid, shiftedStart, shiftedEnd)) {
      return null;
    }
    return { dentistUid: otherUid, reason: "beyond_plot" };
  };

  // Booking reassignment: another chair may absorb displaced visits — validate forward chain there.
  for (const bucket of chairTableAfter) {
    const bucketAppts = bucket.appointments || [];
    if (bucketAppts.length === 0) continue;

    const hasEditingAppt = bucketAppts.some((a) =>
      appointmentMatchesId(a, editingAppointmentId)
    );
    if (hasEditingAppt) continue;

    const bucketSorted = [...bucketAppts].sort((a, b) => {
      const aRow = getAppointmentDisplayRangeMinutes(a);
      const bRow = getAppointmentDisplayRangeMinutes(b);
      return (aRow?.start ?? 0) - (bRow?.start ?? 0);
    });

    const { updates: bucketUpdates, exceedsDayEnd: bucketExceedsDayEnd } =
      computeCompressibleTimelineShiftUpdates(bucketSorted, {
        isCompressible: (_apt, st) => isCompressiblePendingStatus(st),
      });

    if (bucketExceedsDayEnd) {
      return {
        violation: {
          dentistUid:
            editingDentistId ||
            resolveAppointmentDentistUid(bucketSorted[0]) ||
            "",
          reason: "day_end",
        },
        updatesById: new Map(),
      };
    }

    for (const appt of bucketSorted) {
      const finalRange = resolveCompressedFinalRangeMinutes(appt, bucketUpdates);
      if (!finalRange) continue;
      const origRange = getAppointmentDisplayRangeMinutes(appt);
      if (
        origRange &&
        origRange.start === finalRange.start &&
        origRange.end === finalRange.end
      ) {
        continue;
      }
      const cascadeViolation = violationForShiftedPending(
        appt,
        finalRange.start,
        finalRange.end
      );
      if (cascadeViolation) {
        return {
          violation: { ...cascadeViolation, reason: "forward_cascade" },
          updatesById: new Map(),
        };
      }
    }
  }

  const mergedUpdates = new Map();

  const saveChairNum =
    afterChairNum ??
    findChairNumForAppointment(chairTableAfter, editingAppointmentId);

  if (saveChairNum != null) {
    const afterChairAppts = getChairAppointmentsFromTable(
      displayChairTableAfter,
      saveChairNum
    );
    const { exceedsDayEnd, updatesById } = collectChairBucketCascadeUpdates({
      chairAppts: afterChairAppts,
      editingAppointmentId,
      newStartMin,
      newEndMin,
      origStart,
    });
    if (exceedsDayEnd) {
      return {
        violation: {
          dentistUid: editingDentistId || "",
          reason: "day_end",
        },
        updatesById: new Map(),
      };
    }
    updatesById.forEach((value, key) => mergedUpdates.set(key, value));
  }

  for (const bucket of displayChairTableAfter) {
    if (bucket.chairNum === saveChairNum) continue;
    const bucketAppts = bucket.appointments || [];
    if (
      bucketAppts.length === 0 ||
      bucketAppts.some((a) => appointmentMatchesId(a, editingAppointmentId))
    ) {
      continue;
    }

    const bucketSorted = [...bucketAppts].sort((a, b) => {
      const aRow = getAppointmentDisplayRangeMinutes(a);
      const bRow = getAppointmentDisplayRangeMinutes(b);
      return (aRow?.start ?? 0) - (bRow?.start ?? 0);
    });

    const { updates: bucketUpdates, exceedsDayEnd } = computeCompressibleTimelineShiftUpdates(
      bucketSorted,
      {
        isCompressible: (_apt, st) => isCompressiblePendingStatus(st),
      }
    );

    if (exceedsDayEnd) {
      return {
        violation: {
          dentistUid:
            editingDentistId ||
            resolveAppointmentDentistUid(bucketSorted[0]) ||
            "",
          reason: "day_end",
        },
        updatesById: new Map(),
      };
    }

    for (const appt of bucketSorted) {
      const finalRange = resolveCompressedFinalRangeMinutes(appt, bucketUpdates);
      if (!finalRange) continue;
      const id = appt.id || appt.appointmentId;
      if (!id) continue;
      const startTime = formatClock12FromMinutes(finalRange.start);
      const endTime = formatClock12FromMinutes(finalRange.end);
      const origRange = getAppointmentDisplayRangeMinutes(appt);
      if (
        origRange &&
        origRange.start === finalRange.start &&
        origRange.end === finalRange.end
      ) {
        continue;
      }
      mergedUpdates.set(id, {
        startTime,
        endTime,
        duration: Math.max(1, finalRange.end - finalRange.start),
      });
    }
  }

  // Cross-chair same-dentist conflict: ensure no cascaded appointment's new time
  // overlaps with a fixed (non-cascaded) appointment from the same dentist.
  const shiftedIds = new Set(mergedUpdates.keys());
  updatedActive.forEach((a) => {
    if (appointmentMatchesId(a, editingAppointmentId)) {
      if (a.id) shiftedIds.add(a.id);
      if (a.appointmentId) shiftedIds.add(a.appointmentId);
    }
  });

  for (const [shiftedId, timeUpdate] of mergedUpdates) {
    const shiftedAppt = updatedActive.find(
      (a) => (a.id || a.appointmentId) === shiftedId
    );
    if (!shiftedAppt) continue;
    const dentistUid = resolveAppointmentDentistUid(shiftedAppt);
    if (!dentistUid) continue;
    const shiftedStart = parseClockLabelToMinutes(timeUpdate.startTime);
    const shiftedEnd = parseClockLabelToMinutes(timeUpdate.endTime);
    if (!Number.isFinite(shiftedStart) || !Number.isFinite(shiftedEnd)) continue;

    for (const fixedAppt of updatedActive) {
      const fixedId = fixedAppt.id || fixedAppt.appointmentId;
      if (!fixedId || shiftedIds.has(fixedId)) continue;
      if (resolveAppointmentDentistUid(fixedAppt) !== dentistUid) continue;
      const fixedRange = getAppointmentDisplayRangeMinutes(fixedAppt);
      if (!fixedRange) continue;
      if (shiftedStart < fixedRange.end && shiftedEnd > fixedRange.start) {
        return {
          violation: {
            dentistUid,
            reason: "same_dentist_conflict",
            blockerAppt: fixedAppt,
          },
          updatesById: new Map(),
        };
      }
    }
  }

  return { violation: null, updatesById: mergedUpdates };
}

export function findEditChairCascadeScheduleViolation(params = {}) {
  const { violation } = simulateEditChairCascade(params);
  return violation;
}

/** True when [startMin, endMin) lies inside a contiguous plotted window (merged adjacent slots). */
export function isRangeWithinPlottedSchedule(scheduleDocData, dentistUid, startMin, endMin) {
  if (
    !Number.isFinite(startMin) ||
    !Number.isFinite(endMin) ||
    endMin <= startMin
  ) {
    return false;
  }
  return getMergedPlottedScheduleRangesForDentist(scheduleDocData, dentistUid).some(
    (r) => r.start <= startMin && r.end >= endMin
  );
}

/** True when the proposed visit overlaps gaps outside the dentist's plotted hours. */
export function doesRangeViolateDentistPlottedSchedule(
  scheduleDocData,
  dentistUid,
  startMin,
  endMin
) {
  if (
    !dentistUid ||
    !scheduleDocData ||
    !Number.isFinite(startMin) ||
    !Number.isFinite(endMin) ||
    endMin <= startMin
  ) {
    return false;
  }
  if (!dentistHasPlottedSchedule(scheduleDocData, dentistUid)) return false;
  const unavailable = getDentistScheduleUnavailableRanges(scheduleDocData, dentistUid);
  if (!Array.isArray(unavailable) || unavailable.length === 0) return false;
  return unavailable.some((r) => startMin < r.end && r.start < endMin);
}

export function filterSlotLabelsToPlottedSchedule(
  slotLabels,
  scheduleDocData,
  dentistUid,
  settings
) {
  if (!Array.isArray(slotLabels)) return [];
  if (!isDentistSchedulePlottingEnabled(settings) || !dentistUid) {
    return slotLabels;
  }
  const entry = getDentistScheduleEntry(scheduleDocData, dentistUid);
  if (!entry) return [];
  const plottedRanges = entry.availableSlots
    .map(parsePlottedSlotLabelRange)
    .filter(Boolean);
  if (plottedRanges.length === 0) return [];
  return slotLabels.filter((label) => {
    const range = parsePlottedSlotLabelRange(label);
    if (!range) return false;
    return plottedRanges.some((plotted) => plottedSlotRangesOverlap(range, plotted));
  });
}

/**
 * Returns minute-based unavailable ranges for a dentist based on their plotted schedule.
 * These are the complement of their available slots — times the dentist is NOT scheduled.
 * Used as hard blocks in the patient service duration precheck so services cannot push
 * the appointment end past the dentist's scheduled working hours or into schedule gaps.
 * Returns null if the dentist has no schedule entry (no restriction applied).
 */
export function getDentistScheduleUnavailableRanges(scheduleDocData, dentistUid) {
  const entry = getDentistScheduleEntry(scheduleDocData, dentistUid);
  if (!entry || !Array.isArray(entry.availableSlots) || entry.availableSlots.length === 0) {
    return null;
  }
  const sorted = entry.availableSlots
    .map(parsePlottedSlotLabelRange)
    .filter(Boolean)
    .sort((a, b) => a.start - b.start);
  if (sorted.length === 0) return null;

  // Merge contiguous/overlapping available ranges
  const merged = sorted.reduce((acc, next) => {
    if (acc.length === 0) return [{ ...next }];
    const last = acc[acc.length - 1];
    if (next.start <= last.end) { last.end = Math.max(last.end, next.end); return acc; }
    acc.push({ ...next });
    return acc;
  }, []);

  // Complement: everything NOT in the available ranges = unavailable
  const unavailable = [];
  let cursor = 0;
  for (const r of merged) {
    if (r.start > cursor) unavailable.push({ start: cursor, end: r.start });
    cursor = r.end;
  }
  // Everything after the last scheduled slot is also unavailable
  if (cursor < 24 * 60) unavailable.push({ start: cursor, end: 24 * 60 });
  return unavailable;
}

/**
 * When plot workflow is enabled, returns plotted half-hour labels for the date.
 * Returns null when plotting is disabled (use legacy slot generation).
 */
export function getPlottedAvailableSlotsForDentist(
  scheduleDocData,
  dentistUid,
  settings
) {
  if (!isDentistSchedulePlottingEnabled(settings) || !dentistUid) {
    return null;
  }
  const entry = getDentistScheduleEntry(scheduleDocData, dentistUid);
  if (!entry) return [];
  return entry.availableSlots;
}

/** Map each slot label to the other dentist uid occupying it on the same chair. */
export function getBlockedSlotOwnerMapAcrossDates(
  schedulesByDateKey,
  { dateKeys = [], excludeDentistUid = null, chairNumber } = {}
) {
  const ownerBySlot = new Map();
  if (!schedulesByDateKey || !Array.isArray(dateKeys) || dateKeys.length === 0) {
    return ownerBySlot;
  }
  const chair = Number(chairNumber);
  if (!Number.isFinite(chair) || chair <= 0) return ownerBySlot;

  dateKeys.forEach((dateKey) => {
    const scheduleData = schedulesByDateKey[dateKey] || {};
    Object.entries(scheduleData).forEach(([uid, entry]) => {
      if (!uid || uid === excludeDentistUid) return;
      if (!entry || typeof entry !== "object") return;
      const entryChair = Number(entry.chairNumber);
      if (!Number.isFinite(entryChair) || entryChair !== chair) return;
      normalizeAvailableSlots(entry.availableSlots).forEach((slot) => {
        const existing = ownerBySlot.get(slot);
        if (!existing) {
          ownerBySlot.set(slot, uid);
        } else if (existing !== uid) {
          ownerBySlot.set(slot, `${existing}::${uid}`);
        }
      });
    });
  });
  return ownerBySlot;
}

/**
 * Slots the same dentist already occupies on a different chair for the selected dates.
 * One dentist cannot be in two chairs at the same time.
 */
export function getSameDentistSlotsOnOtherChairsAcrossDates(
  schedulesByDateKey,
  { dateKeys = [], dentistUid, chairNumber } = {}
) {
  const blockedBySlot = new Map();
  if (!schedulesByDateKey || !dentistUid || !Array.isArray(dateKeys) || dateKeys.length === 0) {
    return blockedBySlot;
  }
  const targetChair = Number(chairNumber);
  if (!Number.isFinite(targetChair) || targetChair <= 0) return blockedBySlot;

  dateKeys.forEach((dateKey) => {
    const entry = getDentistScheduleEntry(schedulesByDateKey[dateKey], dentistUid);
    if (!entry) return;
    const entryChair = Number(entry.chairNumber);
    if (!Number.isFinite(entryChair) || entryChair === targetChair) return;
    entry.availableSlots.forEach((slot) => {
      blockedBySlot.set(slot, `self:chair:${entryChair}`);
    });
  });
  return blockedBySlot;
}

/** Merge chair conflicts (other dentists) and same-dentist other-chair conflicts. */
export function getScheduleModalBlockedSlotOwnerMap(
  schedulesByDateKey,
  { dateKeys = [], dentistUid, chairNumber } = {}
) {
  const map = getBlockedSlotOwnerMapAcrossDates(schedulesByDateKey, {
    dateKeys,
    excludeDentistUid: dentistUid,
    chairNumber,
  });
  getSameDentistSlotsOnOtherChairsAcrossDates(schedulesByDateKey, {
    dateKeys,
    dentistUid,
    chairNumber,
  }).forEach((ownerKey, slot) => {
    if (!map.has(slot)) {
      map.set(slot, ownerKey);
    }
  });
  return map;
}

/** Slots already plotted for other dentists on the same chair for one date. */
export function getOccupiedSlotsByOtherDentistsOnChair(
  scheduleDocData,
  { excludeDentistUid = null, chairNumber } = {}
) {
  const occupied = new Set();
  const chair = Number(chairNumber);
  if (!scheduleDocData || !Number.isFinite(chair) || chair <= 0) {
    return occupied;
  }
  Object.entries(scheduleDocData).forEach(([uid, entry]) => {
    if (!uid || uid === excludeDentistUid) return;
    if (!entry || typeof entry !== "object") return;
    const entryChair = Number(entry.chairNumber);
    if (!Number.isFinite(entryChair) || entryChair !== chair) return;
    normalizeAvailableSlots(entry.availableSlots).forEach((slot) => occupied.add(slot));
  });
  return occupied;
}

/** Union of chair-conflicting slots across multiple selected dates. */
export function getBlockedSlotsAcrossDates(
  schedulesByDateKey,
  { dateKeys = [], excludeDentistUid = null, chairNumber } = {}
) {
  return new Set(
    getBlockedSlotOwnerMapAcrossDates(schedulesByDateKey, {
      dateKeys,
      excludeDentistUid,
      chairNumber,
    }).keys()
  );
}

/** Existing plotted slots/chair for a dentist across selected dates (intersection). */
export function getDentistPlottedSlotsForDates(
  schedulesByDateKey,
  { dateKeys = [], dentistUid } = {}
) {
  if (!dentistUid || !Array.isArray(dateKeys) || dateKeys.length === 0) {
    return { availableSlots: [], chairNumber: null };
  }

  let slotSet = null;
  let chairNumber = null;

  for (const dateKey of dateKeys) {
    const entry = getDentistScheduleEntry(schedulesByDateKey?.[dateKey], dentistUid);
    if (!entry) {
      return { availableSlots: [], chairNumber: null };
    }
    if (slotSet === null) {
      slotSet = new Set(entry.availableSlots);
      chairNumber = entry.chairNumber;
    } else {
      slotSet = new Set(entry.availableSlots.filter((slot) => slotSet.has(slot)));
      if (entry.chairNumber !== chairNumber) {
        chairNumber = null;
      }
    }
  }

  if (!slotSet || slotSet.size === 0) {
    return { availableSlots: [], chairNumber: null };
  }

  return {
    availableSlots: STANDARD_OFFICE_SLOT_LABELS.filter((label) => slotSet.has(label)),
    chairNumber,
  };
}

export function subscribeToDentistSchedulesForDate(dateKey, callback) {
  if (!dateKey || typeof callback !== "function") {
    return () => {};
  }
  const ref = getDentistPlotDocRef(dateKey);
  return onSnapshot(
    ref,
    (snapshot) => {
      callback(
        snapshot.exists()
          ? sanitizeDentistPlotDocData(snapshot.data() || {})
          : {}
      );
    },
    () => callback({})
  );
}

/**
 * Plot subscription that reports snapshot metadata (for dashboard chair grid).
 * Callback: (plotData, { fromCache, hasPendingWrites, isError }).
 */
export function subscribeToDentistSchedulesForDateWithMeta(dateKey, callback) {
  if (!dateKey || typeof callback !== "function") {
    return () => {};
  }
  const ref = getDentistPlotDocRef(dateKey);
  return onSnapshot(
    ref,
    { includeMetadataChanges: true },
    (snapshot) => {
      callback(
        snapshot.exists()
          ? sanitizeDentistPlotDocData(snapshot.data() || {})
          : {},
        {
          fromCache: snapshot.metadata.fromCache,
          hasPendingWrites: snapshot.metadata.hasPendingWrites,
          isError: false,
        }
      );
    },
    () =>
      callback(
        {},
        { fromCache: true, hasPendingWrites: false, isError: true }
      )
  );
}

/** Real-time set of YYYY-MM-DD keys where the dentist has a plotted schedule. */
export function subscribeToPlottedDatesForDentist(dentistUid, callback) {
  if (!dentistUid || typeof callback !== "function") {
    callback(new Set());
    return () => {};
  }
  const ref = collection(db, CLINIC_SCHEDULING_COLLECTION);
  return onSnapshot(
    ref,
    (snapshot) => {
      const dates = new Set();
      snapshot.forEach((docSnap) => {
        if (!docSnap.id.startsWith(DENTIST_PLOT_DOC_PREFIX)) return;
        const dateKey = docSnap.id.slice(DENTIST_PLOT_DOC_PREFIX.length);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) return;
        if (dentistHasPlottedSchedule(docSnap.data(), dentistUid)) {
          dates.add(dateKey);
        }
      });
      callback(dates);
    },
    () => callback(new Set())
  );
}

/**
 * Build a map of chairNum → (slotLabel → dentistUid) for all dentists in a
 * schedule doc, excluding the dentist being assigned.
 */
function buildChairOccupancyMap(scheduleData, excludeDentistUid, chairCount) {
  const map = new Map();
  for (let c = 1; c <= chairCount; c++) map.set(c, new Map());
  if (!scheduleData) return map;
  Object.entries(scheduleData).forEach(([uid, entry]) => {
    if (!uid || uid === excludeDentistUid || !entry || typeof entry !== "object") return;
    const chair = Number(entry.chairNumber);
    if (!Number.isFinite(chair) || chair <= 0 || !map.has(chair)) return;
    normalizeAvailableSlots(entry.availableSlots).forEach((slot) => {
      map.get(chair).set(slot, uid);
    });
  });
  return map;
}

function dentistSlotsFitOnChair(
  dentistUid,
  chairNum,
  normalizedSlots,
  scheduleData,
  chairCount
) {
  const chair = Number(chairNum);
  if (!dentistUid || !Number.isFinite(chair) || chair <= 0) return false;
  const occupancy = buildChairOccupancyMap(scheduleData, dentistUid, chairCount);
  const slotMap = occupancy.get(chair) || new Map();
  return !normalizedSlots.some((slot) => slotMap.has(slot));
}

function countSlotsFittingOnChair(
  dentistUid,
  chairNum,
  normalizedSlots,
  scheduleData,
  chairCount
) {
  const occupancy = buildChairOccupancyMap(scheduleData, dentistUid, chairCount);
  const slotMap = occupancy.get(chairNum) || new Map();
  return normalizedSlots.filter((slot) => !slotMap.has(slot)).length;
}

/**
 * Pick the best chair for a dentist plot. Dentist plots on the same chair can
 * overlap (only patient appointments consume a chair). Prefer a chair where all
 * slots fit without another dentist's plot; otherwise the chair with the most
 * non-conflicting slots (e.g. Chair 2 when Chair 1 is blocked by Dr. Daniel).
 */
function pickBestChairForDentistPlots(
  dentistUid,
  normalizedSlots,
  existingEntry,
  existingScheduleData,
  chairCount
) {
  const existingChair = existingEntry?.chairNumber;
  const tryOrder = [];
  if (existingChair) tryOrder.push(existingChair);
  for (let c = 1; c <= chairCount; c += 1) {
    if (!tryOrder.includes(c)) tryOrder.push(c);
  }

  for (const chair of tryOrder) {
    if (
      dentistSlotsFitOnChair(
        dentistUid,
        chair,
        normalizedSlots,
        existingScheduleData,
        chairCount
      )
    ) {
      return chair;
    }
  }

  let bestChair = existingChair || 1;
  let bestFit = -1;
  for (let c = 1; c <= chairCount; c += 1) {
    const fitCount = countSlotsFittingOnChair(
      dentistUid,
      c,
      normalizedSlots,
      existingScheduleData,
      chairCount
    );
    if (fitCount > bestFit) {
      bestFit = fitCount;
      bestChair = c;
    }
  }
  return bestChair;
}

/**
 * Resolve chair for saving a dentist plot. Tries strict auto-assign first; if
 * another dentist's plot blocks every chair, still saves using the best-fit chair
 * so staff can extend hours (e.g. Associate 9:00–12:00 on Chair 2 while Daniel
 * uses Chair 1).
 */
function getPlottedMinuteSpanForSlots(availableSlots) {
  const parsed = normalizeAvailableSlots(availableSlots)
    .map(parsePlottedSlotLabelRange)
    .filter(Boolean);
  if (!parsed.length) return null;
  return {
    start: Math.min(...parsed.map((r) => r.start)),
    end: Math.max(...parsed.map((r) => r.end)),
  };
}

function applyChairMovesToScheduleData(scheduleData, moves = []) {
  const next = { ...scheduleData };
  moves.forEach(({ dentistUid, newChairNumber }) => {
    const entry = next[dentistUid];
    if (!entry || typeof entry !== "object") return;
    next[dentistUid] = { ...entry, chairNumber: newChairNumber };
  });
  return next;
}

function getDentistAppointmentMinutesOnChair(chairTable, dentistUid, chairNum) {
  const bucket = (Array.isArray(chairTable) ? chairTable : []).find(
    (entry) => entry.chairNum === chairNum
  );
  if (!bucket) return 0;
  return (Array.isArray(bucket.appointments) ? bucket.appointments : []).reduce(
    (sum, appt) => {
      if (resolveAppointmentDentistUid(appt) !== dentistUid) return sum;
      const range = getAppointmentRangeMinutesForChair(appt);
      return range ? sum + (range.end - range.start) : sum;
    },
    0
  );
}

function getBookingTimestampForAnchor(appt = {}) {
  const id = appt.appointmentId || appt.id || "";
  const lastUnderscore = id.lastIndexOf("_");
  if (lastUnderscore === -1) return 0;
  const ts = Number(id.slice(lastUnderscore + 1));
  return Number.isFinite(ts) ? ts : 0;
}

function getChairAnchoredByFirstAppointment(chairTable, dentistUid) {
  let best = null;

  (Array.isArray(chairTable) ? chairTable : []).forEach(({ chairNum, appointments }) => {
    (Array.isArray(appointments) ? appointments : []).forEach((appt) => {
      if (resolveAppointmentDentistUid(appt) !== dentistUid) return;
      const range = getAppointmentRangeMinutesForChair(appt);
      if (!range) return;

      const candidate = {
        chairNum,
        start: range.start,
        bookingTs: getBookingTimestampForAnchor(appt),
      };

      if (!best) {
        best = candidate;
        return;
      }

      if (candidate.start < best.start) {
        best = candidate;
        return;
      }

      if (candidate.start === best.start && candidate.bookingTs < best.bookingTs) {
        best = candidate;
      }
    });
  });

  return best ? best.chairNum : null;
}

/** Chair where this dentist's patient appointments land for the day (if any). */
function getChairAnchoredByAppointments(chairTable, dentistUid, chairCount) {
  let bestChair = null;
  let bestMinutes = 0;
  for (let chair = 1; chair <= chairCount; chair += 1) {
    const minutes = getDentistAppointmentMinutesOnChair(chairTable, dentistUid, chair);
    if (minutes > bestMinutes) {
      bestMinutes = minutes;
      bestChair = chair;
    }
  }
  return bestMinutes > 0 ? bestChair : null;
}

/**
 * Two-chair clinics: long-day plots (e.g. 9:00–6:00) → chair 1. Other dentists use
 * the chair where their patient appointments already sit so "Available" appears on
 * the same column (e.g. Kahl 9:00–9:30 on chair 2 above Hannah at 9:30–10:00).
 */
function rebalanceTwoChairPlotAssignments(
  scheduleData,
  appointmentsForDate = [],
  chairCount,
  savingDentistUid = null
) {
  if (!chairCount || chairCount < 2) return [];

  const activeAppointments = (Array.isArray(appointmentsForDate) ? appointmentsForDate : [])
    .filter((appt) => isBlockingAppointmentStatus(appt?.status));
  if (activeAppointments.length === 0) return [];

  const dentistsWithAppointments = new Set(
    activeAppointments
      .map((appt) => resolveAppointmentDentistUid(appt))
      .filter(Boolean)
  );
  if (dentistsWithAppointments.size === 0) return [];

  const plottedUids = Object.keys(scheduleData || {}).filter((uid) =>
    dentistHasPlottedSchedule(scheduleData, uid) && dentistsWithAppointments.has(uid)
  );
  if (plottedUids.length < 2) return [];

  const spans = plottedUids
    .map((uid) => {
      const entry = getDentistScheduleEntry(scheduleData, uid);
      const span = getPlottedMinuteSpanForSlots(entry?.availableSlots || []);
      return span ? { uid, span } : null;
    })
    .filter(Boolean);

  let wideDay = spans.reduce((best, row) => {
    const dur = row.span.end - row.span.start;
    const bestDur = best ? best.span.end - best.span.start : 0;
    return dur > bestDur ? row : best;
  }, null);

  const savingRow = savingDentistUid && dentistsWithAppointments.has(savingDentistUid)
    ? spans.find((row) => row.uid === savingDentistUid)
    : null;
  if (
    savingRow &&
    savingRow.span.end - savingRow.span.start >= 180
  ) {
    wideDay = savingRow;
  }

  if (!wideDay || wideDay.span.end - wideDay.span.start < 180) return [];

  const moves = new Map();
  moves.set(wideDay.uid, 1);

  spans.forEach(({ uid, span }) => {
    if (uid === wideDay.uid) return;
    const duration = span.end - span.start;
    const sharesChair1 =
      duration <= 150 && span.end <= 11 * 60 + 30;
    moves.set(uid, sharesChair1 ? 1 : 2);
  });

  const simulated = applyChairMovesToScheduleData(
    scheduleData,
    [...moves.entries()].map(([dentistUid, newChairNumber]) => ({
      dentistUid,
      newChairNumber,
    }))
  );

  const chairTable = assignAppointmentsToChairsForBooking({
    appointments: appointmentsForDate,
    chairCount,
    scheduleDocData: simulated,
    preferExistingChairAnchors: false,
  });

  spans.forEach(({ uid, span }) => {
    if (uid === wideDay.uid) return;
    const apptChair = getChairAnchoredByAppointments(chairTable, uid, chairCount);
    if (apptChair != null) {
      moves.set(uid, apptChair);
      return;
    }
    const duration = span.end - span.start;
    const sharesChair1 =
      duration <= 150 && span.end <= 11 * 60 + 30;
    moves.set(uid, sharesChair1 ? 1 : 2);
  });

  return [...moves.entries()].map(([dentistUid, newChairNumber]) => ({
    dentistUid,
    newChairNumber,
  }));
}

/** Display/save chair numbers after two-chair rebalance (long day → chair 1). */
export function getDisplayChairNumbersForDate(
  scheduleDocData,
  {
    appointmentsForDate = [],
    chairCount = 0,
    savingDentistUid = null,
    /** When false, keep plotted chairs only (+ two-chair rebalance); do not re-anchor from spillover placement. */
    useAppointmentChairAnchors = true,
  } = {}
) {
  const out = {};
  if (!scheduleDocData || typeof scheduleDocData !== "object") return out;

  Object.keys(scheduleDocData).forEach((uid) => {
    const entry = getDentistScheduleEntry(scheduleDocData, uid);
    if (entry?.chairNumber) out[uid] = entry.chairNumber;
  });

  if (useAppointmentChairAnchors && chairCount && chairCount > 0) {
    const chairTable = assignAppointmentsToChairsForBooking({
      appointments: appointmentsForDate,
      chairCount,
      scheduleDocData,
      preferExistingChairAnchors: true,
    });

    Object.keys(scheduleDocData).forEach((uid) => {
      const anchoredChair = getChairAnchoredByFirstAppointment(chairTable, uid);
      if (Number.isFinite(anchoredChair) && anchoredChair > 0) {
        out[uid] = anchoredChair;
      }
    });
  }

  if (!chairCount || chairCount < 2) return out;

  rebalanceTwoChairPlotAssignments(
    scheduleDocData,
    appointmentsForDate,
    chairCount,
    savingDentistUid
  ).forEach(({ dentistUid, newChairNumber }) => {
    out[dentistUid] = newChairNumber;
  });

  return out;
}

/** Merge plotted schedule with display chair numbers (shared by dashboard and cascade). */
export function buildScheduleWithDisplayChairsForDate(
  plotData,
  appointmentsForDate,
  chairCount,
  options = {}
) {
  return buildScheduleWithDisplayChairs(plotData, appointmentsForDate, chairCount, options);
}

function pickChairForDentistScheduleSave({
  dentistUid,
  normalizedSlots,
  existingEntry,
  existingScheduleData,
  chairCount,
  appointmentsForDate = [],
}) {
  const autoResult = autoAssignChairForDentist({
    dentistUid,
    availableSlots: normalizedSlots,
    chairCount,
    existingScheduleData,
    appointmentsForDate,
  });
  if (autoResult.chairNumber != null) {
    return autoResult;
  }

  const span = getPlottedMinuteSpanForSlots(normalizedSlots);
  let chairNumber = pickBestChairForDentistPlots(
    dentistUid,
    normalizedSlots,
    existingEntry,
    existingScheduleData,
    chairCount
  );
  if (
    chairCount === 2 &&
    span &&
    span.end - span.start >= 180 &&
    countSlotsFittingOnChair(
      dentistUid,
      1,
      normalizedSlots,
      existingScheduleData,
      chairCount
    ) > 0
  ) {
    chairNumber = 1;
  }

  return { chairNumber, schedulesToUpdate: [] };
}

function cloneScheduleDataWithChairMoves(scheduleData, moves = []) {
  const next = { ...scheduleData };
  moves.forEach(({ dentistUid, newChairNumber }) => {
    const entry = next[dentistUid];
    if (!entry || typeof entry !== "object") return;
    next[dentistUid] = { ...entry, chairNumber: newChairNumber };
  });
  return next;
}

/**
 * Try to place the saving dentist on a chair by relocating other plotted dentists
 * whose schedules block that chair.
 */
function tryAssignWithDentistRelocations(
  dentistUid,
  normalizedSlots,
  chairCount,
  existingScheduleData
) {
  for (let targetChair = 1; targetChair <= chairCount; targetChair += 1) {
    const moves = [];
    let simulatedData = { ...existingScheduleData };
    let guard = 0;

    while (guard < chairCount + 2) {
      guard += 1;
      const occupancy = buildChairOccupancyMap(simulatedData, dentistUid, chairCount);
      const slotMap = occupancy.get(targetChair) || new Map();
      const conflictSlots = normalizedSlots.filter((slot) => slotMap.has(slot));
      if (conflictSlots.length === 0) {
        return { chairNumber: targetChair, schedulesToUpdate: moves };
      }

      const conflictUids = [
        ...new Set(conflictSlots.map((slot) => slotMap.get(slot)).filter(Boolean)),
      ];
      let progressed = false;

      for (const conflictUid of conflictUids) {
        const conflictEntry = getDentistScheduleEntry(simulatedData, conflictUid);
        if (!conflictEntry) continue;

        for (let altChair = 1; altChair <= chairCount; altChair += 1) {
          if (altChair === targetChair) continue;
          if (
            dentistSlotsFitOnChair(
              conflictUid,
              altChair,
              conflictEntry.availableSlots,
              simulatedData,
              chairCount
            )
          ) {
            moves.push({ dentistUid: conflictUid, newChairNumber: altChair });
            simulatedData = cloneScheduleDataWithChairMoves(simulatedData, moves);
            progressed = true;
            break;
          }
        }
        if (!progressed) break;
      }

      if (!progressed) break;
    }
  }

  return null;
}

/**
 * Auto-assign a dentist to the first available chair in ascending order (1 → N).
 *
 * Phase 1 – find a completely free chair.
 * Phase 2 – find a chair whose only conflicts are dentists that have ONLY
 *            pending/new appointments, and can themselves be moved to another
 *            free chair.
 *
 * @returns {{ chairNumber: number, schedulesToUpdate: Array<{dentistUid, newChairNumber}> }}
 * @throws  {Error} when no assignment is possible.
 */
export function autoAssignChairForDentist({
  dentistUid,
  availableSlots,
  chairCount,
  existingScheduleData,
  appointmentsForDate = [],
}) {
  const normalizedSlots = normalizeAvailableSlots(availableSlots);
  if (!normalizedSlots.length) throw new Error("No valid time slots provided.");
  if (!Number.isFinite(chairCount) || chairCount <= 0) {
    throw new Error("Configure the number of clinic chairs first.");
  }

  const occupancy = buildChairOccupancyMap(existingScheduleData, dentistUid, chairCount);

  // Phase 1: first chair with no slot conflicts
  for (let chair = 1; chair <= chairCount; chair++) {
    const slotMap = occupancy.get(chair) || new Map();
    if (!normalizedSlots.some((slot) => slotMap.has(slot))) {
      return { chairNumber: chair, schedulesToUpdate: [] };
    }
  }

  // Phase 2: first chair where all conflicts can be resolved by moving
  // pending-only dentists to another chair
  for (let chair = 1; chair <= chairCount; chair++) {
    const slotMap = occupancy.get(chair) || new Map();
    const conflictSlots = normalizedSlots.filter((slot) => slotMap.has(slot));
    if (!conflictSlots.length) continue;

    const conflictDentistUids = new Set(conflictSlots.map((s) => slotMap.get(s)));
    const schedulesToUpdate = [];
    let canResolveAll = true;

    for (const conflictDentistUid of conflictDentistUids) {
      // Reject if any appointment for this dentist on these slots is non-pending
      const hasNonPending = appointmentsForDate.some((appt) => {
        const uid = appt.dentistId || appt.dentistUID || appt.dentistUid;
        if (uid !== conflictDentistUid) return false;
        if (!isBlockingAppointmentStatus(appt.status)) return false;
        const s = (appt.status || "").toLowerCase();
        return s !== "pending" && s !== "new";
      });
      if (hasNonPending) { canResolveAll = false; break; }

      // Find an alternative chair for the conflicting dentist
      const conflictEntry = getDentistScheduleEntry(existingScheduleData, conflictDentistUid);
      const conflictSlotsFull = normalizeAvailableSlots(conflictEntry?.availableSlots || []);
      let altChair = null;
      for (let altC = 1; altC <= chairCount; altC++) {
        if (altC === chair) continue;
        const altSlotMap = occupancy.get(altC) || new Map();
        const hasAltConflict = conflictSlotsFull.some((slot) => {
          const owner = altSlotMap.get(slot);
          return owner && owner !== conflictDentistUid;
        });
        if (!hasAltConflict) { altChair = altC; break; }
      }
      if (!altChair) { canResolveAll = false; break; }
      schedulesToUpdate.push({ dentistUid: conflictDentistUid, newChairNumber: altChair });
    }

    if (canResolveAll) {
      return { chairNumber: chair, schedulesToUpdate };
    }
  }

  const relocated = tryAssignWithDentistRelocations(
    dentistUid,
    normalizedSlots,
    chairCount,
    existingScheduleData
  );
  if (relocated) return relocated;

  return { chairNumber: null, schedulesToUpdate: [] };
}

export async function saveDentistSchedulePlot({
  dentistUid,
  dateKeys,
  availableSlots,
  chairNumber = null,
  chairCount = null,
  chairCountByDateKey = null,
  appointmentsByDateKey = {},
  updatedByUid = null,
}) {
  if (!dentistUid || !Array.isArray(dateKeys) || dateKeys.length === 0) {
    throw new Error("Dentist and at least one date are required.");
  }
  const normalizedSlots = normalizeAvailableSlots(availableSlots);

  // Clearing schedule: remove entries for each date
  if (normalizedSlots.length === 0) {
    for (const dateKey of dateKeys) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) continue;

      const scheduleRef = getDentistPlotDocRef(dateKey);
      const scheduleSnap = await getDoc(scheduleRef);
      if (scheduleSnap.exists() && scheduleSnap.data()?.[dentistUid]) {
        await setDoc(scheduleRef, { [dentistUid]: deleteField() }, { merge: true });
      }

      const unavailableRef = doc(db, "unavailableTimeSlots", dateKey);
      const unavailableSnap = await getDoc(unavailableRef);
      if (unavailableSnap.exists() && unavailableSnap.data()?.[dentistUid]) {
        await setDoc(unavailableRef, { [dentistUid]: deleteField() }, { merge: true });
      }
    }
    return;
  }

  const unavailableSlots = computeUnavailableSlotsFromAvailable(normalizedSlots);

  for (const dateKey of dateKeys) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateKey)) continue;

    const scheduleRef = getDentistPlotDocRef(dateKey);
    const existingSnap = await getDoc(scheduleRef);
    const existingData = existingSnap.exists()
      ? sanitizeDentistPlotDocData(existingSnap.data() || {})
      : {};

    // Determine chair number: explicit legacy input, otherwise keep existing or null.
    let assignedChair;
    let schedulesToUpdate = [];
    const providedChair = Number(chairNumber);
    const perDateCount =
      chairCountByDateKey && typeof chairCountByDateKey === "object"
        ? Number(chairCountByDateKey[dateKey])
        : NaN;
    const resolvedChairCount = Number.isFinite(perDateCount) && perDateCount > 0
      ? perDateCount
      : Number(chairCount);

    if (Number.isFinite(providedChair) && providedChair > 0) {
      // Legacy: explicit chair provided — validate conflicts manually
      const blocked = getOccupiedSlotsByOtherDentistsOnChair(existingData, {
        excludeDentistUid: dentistUid,
        chairNumber: providedChair,
      });
      const conflicts = normalizedSlots.filter((slot) => blocked.has(slot));
      if (conflicts.length > 0) {
        throw new Error(
          `Time slot(s) already assigned on ${dateKey} for Chair ${providedChair}: ${conflicts.join(", ")}`
        );
      }
      assignedChair = providedChair;
    } else {
      if (!Number.isFinite(resolvedChairCount) || resolvedChairCount <= 0) {
        throw new Error(`Configure chairs for ${dateKey} before saving dentist schedules.`);
      }

      assignedChair = null;
    }

    // Apply movements for conflicting dentists that were resolved
    for (const update of schedulesToUpdate) {
      const movedEntry = getDentistScheduleEntry(existingData, update.dentistUid);
      if (!movedEntry) continue;
      const movedPayload = {
        chairNumber: update.newChairNumber,
        availableSlots: movedEntry.availableSlots,
        updatedAt: new Date().toISOString(),
        updatedByUid,
        autoMoved: true,
      };
      await setDoc(scheduleRef, { [update.dentistUid]: movedPayload }, { merge: true });
    }

    const payload = {
      chairNumber: assignedChair,
      availableSlots: normalizedSlots,
      updatedAt: new Date().toISOString(),
      ...(updatedByUid ? { updatedByUid } : {}),
    };

    if (existingSnap.exists()) {
      await updateDoc(scheduleRef, {
        [dentistUid]: payload,
        updatedAt: payload.updatedAt,
        ...(updatedByUid ? { updatedByUid } : {}),
      });
    } else {
      await setDoc(
        scheduleRef,
        {
          [dentistUid]: payload,
          updatedAt: payload.updatedAt,
          ...(updatedByUid ? { updatedByUid } : {}),
        },
        { merge: true }
      );
    }

    const unavailableRef = doc(db, "unavailableTimeSlots", dateKey);
    const unavailableSnap = await getDoc(unavailableRef);
    const unavailableData = unavailableSnap.exists() ? unavailableSnap.data() || {} : {};
    await setDoc(
      unavailableRef,
      {
        ...unavailableData,
        [dentistUid]: unavailableSlots,
      },
      { merge: true }
    );
  }
}

/**
 * Persist table-derived chair assignments back to Firestore for one date.
 * Only updates dentists whose chair number differs from the current doc value.
 */
export async function syncDentistChairNumbersForDate({
  dateKey,
  scheduleDocData,
  chairByDentistUid,
  updatedByUid = null,
} = {}) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateKey || ""))) return;
  if (!scheduleDocData || typeof scheduleDocData !== "object") return;
  if (!chairByDentistUid || typeof chairByDentistUid !== "object") return;

  const updates = {};
  Object.entries(chairByDentistUid).forEach(([uid, chairNum]) => {
    if (!uid) return;
    const nextChair = Number(chairNum);
    if (!Number.isFinite(nextChair) || nextChair <= 0) return;

    const entry = getDentistScheduleEntry(scheduleDocData, uid);
    if (!entry) return;

    const currentChair = Number(entry.chairNumber);
    if (Number.isFinite(currentChair) && currentChair === nextChair) return;

    updates[`${uid}.chairNumber`] = nextChair;
    updates[`${uid}.updatedAt`] = new Date().toISOString();
    if (updatedByUid) {
      updates[`${uid}.updatedByUid`] = updatedByUid;
    }
  });

  if (Object.keys(updates).length === 0) return;

  const scheduleRef = getDentistPlotDocRef(dateKey);
  await updateDoc(scheduleRef, {
    ...updates,
    updatedAt: new Date().toISOString(),
    ...(updatedByUid ? { updatedByUid } : {}),
  });
}

export async function saveClinicChairCount({ chairCount, updatedByUid = null }) {
  const count = Number(chairCount);
  if (!Number.isFinite(count) || count <= 0) {
    throw new Error("Invalid chair count.");
  }
  const ref = doc(db, "clinicScheduling", "settings");
  await setDoc(
    ref,
    {
      chairCount: count,
      rosterDentistIds: [],
      updatedAt: new Date().toISOString(),
      updatedByUid,
    },
    { merge: true }
  );
}
